"use client";

/* ===== easy-tedu · flow screens (dojang / dashboard / settings) ===== */
import { type PointerEvent as ReactPointerEvent, type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { I } from "./icons";
import {
  AppBar, Poster, Track, StatusChip, Logo, ScrollSolidify, Segmented,
  useData, computeNowPlaying,
} from "./ui";
import { fmt, interpViewPosition, isYouTube, controllerDots, atDojang, type DojangView, type DeviceView, type NowPlaying, type StudentView, type ClassView } from "./types";
import { diag, diagOn, bufferedAhead } from "@/lib/diag";
import { useFrame } from "./frame-store";
import { SecLabel, GlassGroup, GlassRow } from "./glass-list";
import type { DemoCourse } from "./demo-courses";
import { YouTubeMirror } from "./youtube-mirror";
import { setMirrorRate } from "./mirror-rate";
import { CALL_FRAMES, CALL_FRAME_KEY, CALL_PLAYER_SIZE_KEY, DEFAULT_CALL_FRAME, canPickCallFrame, readCallFrame, readCallPlayerSize } from "@/lib/call-frame";
import { useCachedVideoSrc, cacheUsage, clearCache } from "@/lib/mirror-cache";
import { retryVideoOnAccessError } from "@/lib/video-access";
import { tkdTitle } from "@/lib/tkd-terms";
import { HOME_LINK_MAX_DEVICES } from "@/lib/home-link";

// phone=숫자만(계정 찾기용) · camera=카메라 허용 계정인가(서버가 판정)
// tour=사용법 둘러보기(2026-08-27 전체 공개) · demo=자동 시연(박람회, 데모·테스트 계정만)
// 🛑 tour 와 demo 는 **다른 플래그**다 — 예전엔 하나였다. 다시 합치지 말 것(lib/flags.ts 참고).
export type Owner = { name: string; email: string; phone: string | null; camera?: boolean; tour?: boolean; demo?: boolean; homeDevice?: boolean; appFullscreen?: boolean; practice?: boolean; lead?: boolean; voice?: boolean };
export type Layout = "card" | "grid";

/* ---------- Dojang select ---------- */
export function DojangScreen({ dojangs, owner, onPick, onLogout, onCreate }: {
  dojangs: DojangView[];
  owner: Owner;
  onPick: (d: DojangView) => void;
  onLogout: () => void;
  onCreate: () => void;
}) {
  const t = useTranslations("control.dojang");
  const tr = useTranslations("control.role");
  return (
    <div className="screen col" style={{ height: "100%" }}>
      {/* 메타태권 브랜드 로고 */}
      <div className="animate-wave" style={{ display: "flex", justifyContent: "center", padding: "20px 0 18px" }}>
        <Logo size={56} />
      </div>
      <AppBar title={t("selectTitle")} sub={owner.name}
        right={<button className="iconbtn iconbtn--plain" onClick={onLogout} aria-label={t("signOut")}><I.logout size={21} /></button>} />
      <div className="frame__scroll col gap-14" style={{ padding: "8px 18px 18px" }}>
        <GlassGroup glass inset={77}>
          {dojangs.map((d) => (
            <GlassRow key={d.id} onClick={() => onPick(d)} nav
              lead={<div style={{ width: 46, height: 46, borderRadius: "var(--r-md)", flex: "none", background: "var(--surface-3)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 19, color: "var(--text-1)" }}>{d.name[0]}</div>}
              title={d.name}
              sub={
                <div className="row gap-6" style={{ alignItems: "center" }}>
                  <span className={"chip " + (d.role === "OWNER" || d.role === "SUPERADMIN" ? "chip--accent" : "chip--off")}>{d.role === "OWNER" ? tr("owner") : d.role === "SUPERADMIN" ? tr("operator") : tr("instructor")}</span>
                  <span className="chip chip--off"><I.monitor size={13} />{t("deviceCount", { count: d.deviceCount ?? "—" })}</span>
                </div>
              }
            />
          ))}
        </GlassGroup>
        <button className="btn btn--ghost" style={{ height: 56, marginTop: 4 }} onClick={onCreate}><I.plus size={20} />{t("createNew")}</button>
      </div>
    </div>
  );
}

// 그리드 미리보기 방식 — "snapshot": 플레이어가 보낸 프레임 이미지(개수 무제한·안 멈춤, 기본).
// "live": 실제 영상(TileMirror). 폰은 영상 여러 개 동시 디코딩/다운로드가 한계라 보통 멈추지만,
// 테스트용으로 이 한 줄만 바꾸면 라이브로 전환된다(TileMirror 코드 보존).
const GRID_MODE: "snapshot" | "live" = "live";

// 라이브 미러 안전장치(자가조정) — 회선이 감당 못 하는 타일을 썸네일로 떨궈 회선을 해방한다.
// 횟수를 세지 않고 "진행 정지 시간"으로 판단 → seek·영상전환 churn 에 안 휘둘림.
const MIRROR_STALE_MS = 2000; // status=playing 인데 이 시간 넘게 보고(ts)가 끊기면 플레이어가 멈춘 것으로 보고 타일 미러도 정지(hidden 미수신 대비)
const STALL_MS = 3500;      // 막힘(waiting 등) 후 이 시간 내 재생 진행 없으면 썸네일 폴백
const RETRY_BASE = 4000;    // 폴백 후 재시도 기본 지연(지수 백오프 시작값)
const RETRY_MAX = 30000;    // 재시도 지연 상한
const RETRY_JITTER = 3000;  // 재시도 지터 — 떨어진 타일들이 동시에 복귀(=재포화)하지 않게 분산

/* 그리드 타일용 라이브 미러 — 재생 중인 기기의 실제 영상을 보고된 위치에 맞춰 음소거 재생.
   단일 미러와 동일한 배속 수렴(soft-sync) 적용 — seek 재버퍼 부하를 줄여 여러 타일 동시 재생 부담을 낮춤. */
export function TileMirror({ device, np, now }: { device: DeviceView; np: NowPlaying; now: number }) {
  const vref = useRef<HTMLVideoElement>(null);
  // device.hidden(플레이어 탭 최소화)이면 실제 재생이 멈춰 있으므로 타일 미러도 정지 — 그리드에서도
  // 최소화된 기기가 "혼자 흐르는" 잔상을 막는다(위치는 interpViewPosition 이 이미 고정).
  // hidden 을 못 받은 경우도 대비 — status=playing 인데 보고(ts)가 MIRROR_STALE_MS 넘게 끊기면 정지.
  // 신선도는 '받은 로컬 시각(rxAt)'으로만 판단한다. 예전엔 device.ts(기기 시계 보정값)를 썼는데,
  // 보정이 어긋나면 보고가 0.5초마다 와도 계속 '오래됨'으로 읽혀 playing 이 영영 false 가 됐다
  // → 그리드·제어화면 미러가 둘 다 멈추고 플레이어만 멀쩡한 증상(실측).
  const stale = now > 0 && device.status === "playing" && device.rxAt > 0 && now - device.rxAt > MIRROR_STALE_MS;
  const playing = device.online && device.status === "playing" && !device.hidden && !stale;
  // 미러본만 재생 — 원본(폰 4K/HEVC) 폴백은 타일 여러 개 동시 디코딩 시 멈춤·회선 포화를 유발. 미러 완성 전엔 빈 소스(타일 배경 폴백 노출).
  const yt = isYouTube(np.vid);
  const mirrorRel = !yt ? (np.vid?.mirror ?? null) : null;
  const pos = interpViewPosition(device, now);
  const posRef = useRef(pos);
  useEffect(() => { posRef.current = pos; }, [pos]);
  // 저속 자동 전환 — 타일도 상세 미러와 동일하게, 회선이 비트레이트를 못 따라가면(기아 반복)
  // 다운로드를 끊어 썸네일 폴백으로 내려간다. 그래야 굶주린 타일이 회선을 계속 점유해
  // 다른 타일·상세 미러까지 같이 멈추는(MOBILE_DIAG 실측) 연쇄를 막는다.
  const [degraded, setDegraded] = useState(false);
  // 로컬 캐시 우선(lib/mirror-cache.ts) — 타일 여러 개가 같은 회선을 당기는 그리드에서 특히 크다.
  const src = useCachedVideoSrc(mirrorRel, np.vid?.mirrorTag ?? null, { streaming: playing && !degraded });
  // 첫 프레임 디코드 전까지는 video 를 숨겨 그 아래 썸네일(Poster)을 노출 — loading 중 video 가
  // 검게 칠해 썸네일 폴백을 덮는 문제(2배속 후 그리드 진입 시 3초 검은 화면) 방지.
  const [hasFrame, setHasFrame] = useState(false);
  const seekAtRef = useRef(0); // 보정 seek 시각 — 드리프트 재seek 쿨다운용
  const waitRef = useRef(false); // 미러가 기기보다 앞서 기기를 기다리며 멈춰 선 중(위치 싱크)
  const loadedSrcRef = useRef(""); // 현재 video 에 실제로 물린 src — 같은 영상 중복 재로드 방지(8타일 동시 시 중복 fetch 절감)
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // 막힘 → 썸네일 폴백 타임아웃
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);  // 썸네일 → 재시도 백오프
  const failCountRef = useRef(0); // 같은 영상에서 연속 폴백 횟수(백오프 지수). 새 영상이면 0 으로
  // 새 영상/배속 → 폴백·백오프 리셋(새 영상은 깨끗한 기회 부여). 타이머도 정리.
  useEffect(() => {
    setDegraded(false);
    failCountRef.current = 0;
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
  }, [src, device.rate]);
  // 영상 바뀌면 소스 교체 — #t= 로 현재 위치부터 받기 시작. degraded 면 소스 해제(회선 해방).
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    if (degraded) { loadedSrcRef.current = ""; setHasFrame(false); v.removeAttribute("src"); v.load(); return; }
    // 일시적 빈 src(목록 재조회로 byId 교체 시 mirror 일시 누락)엔 물려둔 영상 유지 — loadedSrcRef 를 비우면
    // 같은 값 복귀 시 동일 영상을 통째로 재로드(상세 미러와 동일 버그). 진짜 정지는 src 가 새 값으로 바뀐다.
    if (!src) return;
    if (loadedSrcRef.current === src && v.src) return; // 같은 영상 — 재로드 생략(요소 비면 재마운트로 보고 재로드)
    loadedSrcRef.current = src;
    setHasFrame(false); // 새 소스 → 첫 프레임 전까지 썸네일 노출(검은 video 가리기)
    v.src = posRef.current > 1 ? `${src}#t=${Math.floor(posRef.current)}` : src;
    v.load();
  }, [src, degraded]);
  // 표가 없어 영상이 안 열리면(403) 다시 받아 같은 영상을 한 번 더 건다(lib/video-access.ts).
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    return retryVideoOnAccessError(v);
  }, [src]);
  // 첫 프레임 준비되면 video 노출 (그 전까진 opacity:0 으로 썸네일 폴백 유지)
  // ⚠️ src 의존성 필수 — 재마운트 시 리스너가 옛 요소에 남으면 hasFrame 이 영영 false 라
  //    영상이 돌고 있어도 계속 숨겨진다(제어화면과 같은 버그였다).
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    const reveal = () => setHasFrame(true);
    // 이미 프레임이 있으면(재부착 시점에 이미 디코드 완료) 이벤트를 기다리지 않고 바로 노출.
    if (v.readyState >= 2) setHasFrame(true);
    v.addEventListener("loadeddata", reveal); // readyState>=2 = 첫 프레임 디코드 완료
    v.addEventListener("playing", reveal);
    return () => { v.removeEventListener("loadeddata", reveal); v.removeEventListener("playing", reveal); };
  }, [src, device.name]);
  // 재생/일시정지 미러링
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    if (!playing) waitRef.current = false;
    if (playing && !degraded) { if (!waitRef.current) v.play().catch(() => {}); }
    else v.pause();
  }, [playing, degraded]);
  // 안전장치(폴백) — 막힘(waiting/stalled/loadstart) 후 STALL_MS 안에 재생 진행(playing/timeupdate)이
  // 안 오면 "이 회선이 이 타일을 못 감당"으로 보고 썸네일로 폴백(다운로드 끊어 회선 해방).
  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    const clearStall = () => { if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; } };
    const onStall = () => {
      if (!playing || degraded || stallTimerRef.current) return;
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null;
        if (diagOn()) diag(`tile:${device.name}`, `로드 지연 ${STALL_MS}ms 초과 → 썸네일 폴백·다운로드 해방`);
        setDegraded(true);
      }, STALL_MS);
    };
    const onProgress = () => clearStall(); // 진행 재개 — 폴백 보류 해제
    v.addEventListener("waiting", onStall);
    v.addEventListener("stalled", onStall);
    v.addEventListener("loadstart", onStall);
    v.addEventListener("playing", onProgress);
    v.addEventListener("timeupdate", onProgress);
    return () => {
      clearStall();
      v.removeEventListener("waiting", onStall);
      v.removeEventListener("stalled", onStall);
      v.removeEventListener("loadstart", onStall);
      v.removeEventListener("playing", onProgress);
      v.removeEventListener("timeupdate", onProgress);
    };
  }, [playing, degraded, device.name]);
  // 폴백 후 백오프 재시도 — 지수 증가 + 지터로 떨어진 타일들이 동시에 복귀(재포화)하지 않게 분산.
  // 성공하면(영상 전환 시) 위 리셋 effect 가 failCount 를 0 으로 되돌린다.
  useEffect(() => {
    if (!degraded) return;
    const n = failCountRef.current;
    failCountRef.current = n + 1;
    const delay = Math.min(RETRY_BASE * Math.pow(2, n), RETRY_MAX) + Math.random() * RETRY_JITTER;
    retryTimerRef.current = setTimeout(() => { retryTimerRef.current = null; setDegraded(false); }, delay);
    return () => { if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; } };
  }, [degraded]);
  // 위치 싱크 — 작은~중간 어긋남은 배속 수렴(seek 없음 → 재버퍼 부하 없음). 큰 점프만 seek 하되
  // 뒤처짐(앞으로)은 2.5초, 앞섬(뒤로 되감기)은 6초로 비대칭 — 추월 시 연쇄 되감기 방지(상세 미러와 동일).
  useEffect(() => {
    const v = vref.current;
    if (!v || degraded || device.hidden) return; // 최소화 중엔 위치 보정 정지(멈춘 미러를 옛 타깃으로 끌지 않음)
    const base = device.rate || 1;
    const dur = v.duration;
    const target = dur ? Math.min(pos, dur - 0.1) : pos;
    const diff = target - v.currentTime;
    const gap = Math.abs(diff);
    const seekGate = diff > 0 ? 2.5 : 6;
    if (gap > seekGate) {
      // 보정 seek 후 2초 쿨다운 — 재seek 으로 디코더를 계속 리셋해 미러가 얼어붙는 연쇄 차단(상세 미러와 동일).
      if (performance.now() - seekAtRef.current < 2000) { setMirrorRate(v, base); return; }
      seekAtRef.current = performance.now();
      try { v.currentTime = Math.max(0, target); } catch { /* not seekable */ }
      if (waitRef.current) { waitRef.current = false; if (playing) v.play().catch(() => {}); }
      setMirrorRate(v, base);
      return;
    }
    // 앞섬 → 멈춰 서서 기기를 기다린다(상세 미러와 같은 규칙 — 0.4배 슬로모션으로 기어가던 것, 2026-10-16).
    if (diff < -0.4 || (waitRef.current && diff < -0.1)) {
      waitRef.current = true;
      if (!v.paused) v.pause();
      setMirrorRate(v, base);
      return;
    }
    if (waitRef.current) { waitRef.current = false; if (playing) v.play().catch(() => {}); }
    if (gap > 0.3) setMirrorRate(v, diff > 0 ? base * (1 + Math.min(0.5, gap * 0.25)) : base * 0.9);
    else setMirrorRate(v, base);
  }, [pos, device.rate, degraded, device.hidden]);
  // 진단(?diag=1) — 그리드 타일이 멈추는 원인(동시 디코딩 제약·기아·에러) 추적. 기기명으로 타일 구분.
  useEffect(() => {
    if (!diagOn()) return;
    const v = vref.current;
    if (!v) return;
    const tag = `tile:${device.name}`;
    const log = (e: Event) =>
      diag(tag, `${e.type} t=${v.currentTime.toFixed(1)} 버퍼여유=${bufferedAhead(v)}s ready=${v.readyState} net=${v.networkState}${v.error ? ` err=${v.error.code}` : ""} paused=${v.paused}`);
    const evs = ["loadstart", "canplay", "playing", "waiting", "stalled", "suspend", "pause", "error"];
    evs.forEach((t) => v.addEventListener(t, log));
    return () => evs.forEach((t) => v.removeEventListener(t, log));
  }, [device.name]);
  if (yt && np.vid?.youtubeId) {
    return <YouTubeMirror videoId={np.vid.youtubeId} position={pos} playing={playing} rate={device.rate || 1} />;
  }

  return (
    // 지연된 play/playing으로 최소화·정지된 그리드가 다시 움직이지 않게 한다.
    <video
      ref={vref} muted playsInline preload="metadata" disablePictureInPicture
      onPlay={(event) => { if (!playing || degraded) event.currentTarget.pause(); }}
      onPlaying={(event) => { if (!playing || degraded) event.currentTarget.pause(); }}
      // 멈춰야 하는데 계속 흐르면 다시 멈춘다 — WebKit 은 배속을 바꾸는 중에 들어온 pause() 를 놓칠 때가 있다(2026-10-15 시험).
      onTimeUpdate={(event) => { const v = event.currentTarget; if (!v.paused && (!playing || degraded)) v.pause(); }}
      onCanPlay={() => { const v = vref.current; if (v && !waitRef.current && playing && !degraded) v.play().catch(() => {}); }}
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
        opacity: hasFrame && !degraded ? 1 : 0, transition: "opacity .2s ease", // 로드/저속폴백 중엔 숨겨 썸네일 노출
      }}
    />
  );
}

/* 길게 누르기 감지 — 그리드 타일에서 다중선택 모드 진입. 스크롤(이동)·짧은 탭과 구분. */
function useLongPress(onLongPress: () => void, ms = 450) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return {
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      cancel();
      timer.current = setTimeout(() => { timer.current = null; fired.current = true; onLongPress(); }, ms);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      if (!start.current) return;
      if (Math.abs(e.clientX - start.current.x) > 10 || Math.abs(e.clientY - start.current.y) > 10) cancel(); // 스크롤 → 취소
    },
    onPointerUp: cancel,
    cancel,
    // 길게 누름이 막 발생했으면 true (직후 합성 click 1회 무시). 한 번 읽으면 리셋.
    consumeClick: () => { if (fired.current) { fired.current = false; return true; } return false; },
  };
}

/* 선택 모드의 글자 버튼(선택·취소·전체 선택) — 기기 탭과 홈워킹 탭이 같은 모양이어야 해서 여기 하나만 둔다 */
const selBtnStyle: CSSProperties = { background: "none", border: "none", color: "var(--accent)", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: "4px 8px", whiteSpace: "nowrap" };

/* ---------- Device tile (adapts to layout) ---------- */
export function DeviceTile({ device, layout, now = 0, onOpen, onToggle, onLoadDevice, selecting = false, selected = false, onSelect, onLongPress, controlledBy, controlledDot, cam, onLowerHand }: {
  device: DeviceView; layout: Layout; now?: number;
  onOpen: (d: DeviceView) => void;
  onToggle?: (d: DeviceView) => void; // 그리드: 재생/일시정지 (라벨 버튼)
  onLoadDevice?: (d: DeviceView) => void; // 그리드: 대기 타일 '영상 틀기' → 로드 시트 열기(제1과제 1탭)
  selecting?: boolean; // 다중선택 모드 — 탭이 선택 토글로 동작
  selected?: boolean;  // 이 기기가 선택됨
  onSelect?: (d: DeviceView) => void;    // 탭 → 선택 토글
  onLongPress?: (d: DeviceView) => void; // 길게 누름 → 선택 모드 진입 + 선택
  controlledBy?: string; // 마지막 제어자 이름(관장 전용 그리드 배지) — 없으면 미표시
  controlledDot?: string; // 그 사람에게 배정된 점 색(같은 화면 안에서 서로 다르게)
  // 이 기기에 지금 카메라가 걸려 있나 — "보는 중"(기기 카메라를 내가 본다) / "전송 중"(내 얼굴이 저 화면에 뜬다).
  // 🔑 창 안에서만 말하면 안 되는 정보다: 카메라 창을 띄워 둔 채 그리드로 돌아오면 **어느 기기가
  //    걸려 있는지 여태 아무 표시도 없었다.** 특히 전송은 '내 얼굴이 지금 어느 화면에 떠 있나'라,
  //    기기 여러 대인 도장에서 잘못 알면 큰일이다(2026-08-28 · 여러 대 전송을 열면서 같이 붙였다).
  cam?: "view" | "send" | "both";
  onLowerHand?: (d: DeviceView) => void; // 손들기 '확인' — 든 손 내리기
}) {
  const t = useTranslations("control.device");
  const locale = useLocale();
  const handUp = device.handRaised && device.online;
  const frame = useFrame(device.id); // 그리드 스냅샷 — 기기별 구독(해당 타일만 리렌더)
  const { plById, byId } = useData();
  const np = computeNowPlaying(device, plById, byId);
  const dur = np?.vid?.durationSec || device.durationSec || 1;
  const pos = interpViewPosition(device, now); // 250ms 틱 보간 — 2초 하트비트 사이를 메움
  const pct = device.status === "idle" ? 0 : (pos / dur) * 100;
  const StatusIcon = device.online ? I.wifi : I.wifiOff;
  const lp = useLongPress(() => onLongPress?.(device));
  // 누른 직후의 재생/일시정지 — 가운데 ▶ 단추를 기기 보고를 기다리지 않고 바로 바꾼다.
  // 기기(특히 스탠바이미 TV 재생기·유튜브)가 실제로 재생을 시작해 보고하기까지 1~2초 걸려, 재생을 눌러도
  // ▶ 가 그동안 길게 남아 있다가 사라졌다(2026-10-15 신고). 기기 보고가 따라오면 놓고, 안 오면 3초 뒤 진짜 상태로.
  const [tapStatus, setTapStatus] = useState<"playing" | "paused" | null>(null);
  useEffect(() => {
    if (!tapStatus) return;
    if (device.status === tapStatus || !device.online) { setTapStatus(null); return; }
    const tm = setTimeout(() => setTapStatus(null), 3000);
    return () => clearTimeout(tm);
  }, [tapStatus, device.status, device.online]);
  const shownStatus = tapStatus ?? device.status;
  // 🛑 숙제 표시는 여기 없다 — 이 타일은 도장 화면만 그리고, 숙제는 홈워킹 탭이 학생 단위로 말한다.

  if (layout === "grid") {
    return (
      <div
        className="col"
        onPointerDown={lp.onPointerDown}
        onPointerMove={lp.onPointerMove}
        onPointerUp={lp.onPointerUp}
        onPointerCancel={lp.cancel}
        onPointerLeave={lp.cancel}
        style={{
          position: "relative",
          background: "var(--surface-1)",
          border: "1px solid " + (handUp ? "var(--warn)" : selecting && selected ? "var(--accent-line)" : "var(--border)"),
          boxShadow: handUp ? "0 0 0 1px var(--warn)" : selecting && selected ? "inset 0 0 0 2px var(--accent-line)" : device.online ? "var(--shadow-2)" : "var(--shadow-1)",
          borderRadius: "var(--r-md)", overflow: "hidden",
          width: "100%", height: "100%", // 그리드 셀(1fr)을 꽉 채워 타일 크기 통일
        }}>
        {/* 손들기 — 학생이 손 듦. 탭하면 '확인'(손 내리기). 동그라미 없이 ✋ 이모지에 글로우만. 미리보기 버튼 밖(형제) */}
        {handUp && (
          <button
            onClick={(e) => { e.stopPropagation(); onLowerHand?.(device); }}
            aria-label={t("lowerHand")}
            style={{
              position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)",
              zIndex: 4, border: "none", background: "transparent", cursor: "pointer",
              padding: 3, fontSize: 23, lineHeight: 1,
              animation: "tedu-hand-twinkle 1.05s ease-in-out infinite",
            }}>
            ✋
          </button>
        )}
        {/* 카메라 표식 — 좌상단(우상단은 다중선택 체크 자리). 그림 위라 한눈에 든다.
            🛑 **상태 칩(재생 중·대기…) 바로 밑에 놓는다**(2026-08-31 사용자 지적). 둘 다 top:8·left:8
               이라 카메라 칩이 상태 칩을 통째로 덮고 있었다 — 카메라를 켜면 그 기기가 재생 중인지
               대기인지 알 길이 없어진다.
            🔑 상태 칩을 '보는 중'으로 **바꾸지는 않는다**: '재생 중'과 '보는 중'은 동시에 참이라
               (수업 영상을 틀어 둔 채 카메라로 자세를 본다) 하나로 합치면 정보가 준다.
            좌표: 상태 칩이 8 에서 시작해 높이 26(.chip) → 34 에서 끝난다. 4 띄워 38. */}
        {cam && device.online && (
          <span style={{
            position: "absolute", top: 38, left: 8, zIndex: 3, pointerEvents: "none",
            display: "inline-flex", alignItems: "center", gap: 4, height: 22, padding: "0 8px",
            borderRadius: 999, background: "rgba(0,0,0,.62)", border: "1px solid rgba(255,255,255,.28)",
            color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: 1,
          }}>
            {cam === "view" ? <I.video size={12} sw={2.4} /> : <I.monitor size={12} sw={2.4} />}
            {t(cam === "view" ? "camViewing" : cam === "send" ? "camSending" : "camBoth")}
          </span>
        )}
        {/* 다중선택 — 우상단 체크 배지 (탭은 아래 버튼이 처리하도록 pointerEvents:none) */}
        {selecting && (
          <div style={{
            position: "absolute", top: 8, right: 8, zIndex: 3,
            width: 26, height: 26, borderRadius: "50%", pointerEvents: "none",
            background: selected ? "var(--accent)" : "rgba(0,0,0,.45)",
            border: "2px solid " + (selected ? "var(--accent)" : "rgba(255,255,255,.85)"),
            display: "grid", placeItems: "center",
          }}>
            {selected && <I.check size={15} sw={3} style={{ color: "#fff" }} />}
          </div>
        )}
        {/* 미리보기 — 탭하면 상태 동작(재생↔일시정지, 빈 화면=영상 틀기). 세밀 제어는 아래 기기명 줄(›)로 제어화면 */}
        <button
          onClick={() => {
            if (lp.consumeClick()) return; // 길게 누름 직후 합성 클릭 무시
            if (selecting) return onSelect?.(device);
            if (!device.online) return onOpen(device);                    // 오프라인 → 상태 안내
            if (device.status === "idle") return onLoadDevice?.(device);  // 빈 화면 → 영상 틀기
            setTapStatus(device.status === "playing" ? "paused" : "playing");
            onToggle?.(device);                                           // 재생 ↔ 일시정지
          }}
          aria-label={selecting ? t("select") : !device.online ? t("statusAria") : device.status === "idle" ? t("playVideo") : device.status === "playing" ? t("pause") : t("play")}
          style={{ position: "relative", display: "block", width: "100%", aspectRatio: "16 / 9", overflow: "hidden", padding: 0, border: "none", background: "transparent", cursor: "pointer", opacity: device.online ? 1 : 0.5 }}
        >
          {/* 오프라인 — 영상 이미지를 걷어낸다. 미러(video)는 언마운트돼도 그 밑 썸네일이 그대로 비쳐
              "종료했는데 화면이 남아있다"로 읽힌다. 꺼진 기기엔 꺼진 화면을 보여준다. */}
          {!device.online
            ? <div style={{ position: "absolute", inset: 0, background: "var(--surface-2)", display: "grid", placeItems: "center", paddingTop: 22 }}><I.wifiOff size={30} className="dim" /></div>
            // 대기(idle)면 마지막 영상 썸네일(Poster)을 걷어내고 '대기' 화면을 보여준다 — 재생이 끝나거나
            // 전체종료(GO_HOME)해도 컨트롤러엔 마지막 np 가 남아, 게이트가 없으면 지난 영상 썸네일이 계속 비친다
            // ("종료했는데 화면이 남음"). 위 오버레이(라이브/프레임)는 이미 !idle 게이트가 있어 대칭을 맞춘다.
            : np && device.status !== "idle" ? <div style={{ position: "absolute", inset: 0 }}><Poster video={np.vid} h="100%" radius={0} showLabel={false} showDur={false} /></div>
              : <div style={{ position: "absolute", inset: 0, background: "var(--surface-2)", display: "grid", placeItems: "center", paddingTop: 22 }}><I.monitor size={30} className="dim" /></div>}
          {/* 재생/일시정지 중이면 썸네일 위에 라이브 미러 (썸네일은 로드 전 폴백) */}
          {/* 재생 중이면 썸네일 위에 미리보기 — 기본은 스냅샷 이미지(개수 무제한), GRID_MODE="live" 면 실제 영상 */}
          {np && device.online && device.status !== "idle" && (
            GRID_MODE === "live"
              ? <TileMirror device={device} np={np} now={now} />
              : frame
                // eslint-disable-next-line @next/next/no-img-element
                ? <img src={frame} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                : null
          )}
          <div style={{ position: "absolute", top: 8, left: 8 }}><StatusChip device={device} /></div>
          {/* 일시정지 힌트 — 탭하면 재생됨을 알리는 오버레이(pointerEvents:none, 탭은 미리보기 버튼이 처리) */}
          {!selecting && device.online && shownStatus === "paused" && (
            <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.28)", pointerEvents: "none" }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(0,0,0,.5)", backdropFilter: "blur(6px)", display: "grid", placeItems: "center" }}>
                <I.play size={18} style={{ color: "#fff", marginLeft: 2 }} />
              </div>
            </div>
          )}
          {/* 남은시간 — 원래 자리(우하단) 유지. 왼쪽에 있던 제어자 배지는 아래 정보줄로 내려서
              영상 하단이 알약 하나만 걸치게 됐다(발·서기가 보이는 게 중요하다). */}
          {np && device.online && device.status !== "idle" && (
            <span className="tnum" style={{
              position: "absolute", right: 8, bottom: 8, zIndex: 2,
              display: "inline-flex", alignItems: "center", height: 22, padding: "0 8px",
              borderRadius: 999, background: "rgba(0,0,0,.6)", backdropFilter: "blur(4px)",
              color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: 1, pointerEvents: "none",
            }}>{t("timeLeft", { time: fmt(Math.max(0, dur - pos)) })}</span>
          )}
          {/* 진행 — 미리보기 하단 얇은 선(유튜브식). 타일 높이 안 늘려 그리드 균일 유지 */}
          {np && device.online && device.status !== "idle" && (
            <span aria-hidden style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: "rgba(255,255,255,.28)", pointerEvents: "none" }}>
              <span style={{ display: "block", height: "100%", width: `${pct}%`, background: "var(--accent)" }} />
            </span>
          )}
        </button>
        {/* 하단 — 이름/상태(탭=제어 열기) + 재생 진행바 + 상태별 라벨 액션 버튼(미리보기 이중탭 대체) */}
        <div className="col" style={{ padding: "10px 12px 12px", width: "100%", flex: 1, gap: 8 }}>
          {/* data-tour="device-open" — 사용법 둘러보기가 '조종판 여는 자리'로 잡는다.
              🛑 카드 전체가 아니라 **이 이름 줄**이어야 한다. 카드 윗쪽 그림을 누르면 재생/일시정지
                 토글이라, 카드에 표식을 달면 안내대로 눌렀는데 영상이 멈춘다(2026-08-05 실제로 멈춤). */}
          <button data-tour="device-open" onClick={() => {
            if (lp.consumeClick()) return;
            if (selecting) return onSelect?.(device);
            onOpen(device);
          }} className="col" style={{ gap: 3, width: "100%", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}>
            {/* 제목(기기명) — 부제(영상)보다 앞서게: 크게·두껍게·자간 좁힘 */}
            <div className="row" style={{ width: "100%", alignItems: "center" }}>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{device.name}</span>
              <I.chevR size={15} className="dim" style={{ flex: "none" }} />
            </div>
            {/* 오프라인이면 영상 제목이 아니라 '연결 끊김' — 꺼진 기기에 영상 이름이 남으면 아직 트는 줄로 읽힌다 */}
            {/* 영상 제목 + 제어자 — 제어자는 원래 비어 있던 오른쪽 끝에 얹는다(카드 높이 변화 없음). */}
            <div className="row" style={{ width: "100%", alignItems: "center", gap: 6 }}>
              <span className="muted" style={{ flex: 1, minWidth: 0, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{!device.online ? t("disconnected") : np ? tkdTitle(np.vid?.title, locale) : t("notPlaying")}</span>
              {controlledBy && device.online && (
                <span style={{ flex: "none", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: "var(--text-2)", maxWidth: "45%" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", flex: "none", background: controlledDot ?? "#9AA0A6" }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{controlledBy}</span>
                </span>
              )}
            </div>
          </button>
        </div>
      </div>
    );
  }

  // card (rich)
  return (
    <button onClick={() => onOpen(device)} className="col" style={{
      textAlign: "left", background: "var(--surface-1)", border: "1px solid var(--border)",
      borderRadius: "var(--r-lg)", padding: 14, cursor: "pointer", gap: 13, width: "100%", opacity: device.online ? 1 : 0.66,
    }}>
      <div className="row gap-12" style={{ width: "100%" }}>
        <div className="row gap-6" style={{ flex: 1, minWidth: 0, alignItems: "center" }}>
          <StatusIcon size={17} style={{ color: device.online ? "var(--live)" : "var(--text-3)" }} sw={2} />
          <div className="col gap-2" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{device.name}</div>
            {device.room && <div className="muted" style={{ fontSize: 12.5 }}>{device.room}</div>}
          </div>
        </div>
        {device.touchLocked && <I.lock size={15} style={{ color: "var(--warn)", flex: "none" }} aria-label={t("touchLocked")} />}
        <StatusChip device={device} />
      </div>
      {np ? (
        <div className="row gap-12" style={{ width: "100%" }}>
          <Poster video={np.vid} w={84} h={56} showLabel={false} radius={10} />
          <div className="col gap-6" style={{ flex: 1, minWidth: 0, justifyContent: "center" }}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tkdTitle(np.vid?.title, locale)}</span>
              <span className="mono dim" style={{ fontSize: 12 }}>{np.pl.name}</span>
            </div>
            <Track value={pct} h={5} />
            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="mono muted" style={{ fontSize: 11.5 }}>{fmt(pos)} / {fmt(dur)}</span>
              <span className="muted" style={{ fontSize: 11.5 }}>{np.index + 1}/{np.pl.items.length}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="row gap-10" style={{ padding: "6px 0", color: "var(--text-3)", fontSize: 13 }}>
          <I.monitor size={18} />{t("noVideoTapLoad")}
        </div>
      )}
    </button>
  );
}

/* ---------- Group header (재생 중 / 대기 / 오프라인 섹션 구분) ---------- */
function GroupHead({ title, count, first = false, icon }: { title: string; count: number; first?: boolean; icon?: React.ReactNode }) {
  return (
    <div className="row" style={{ alignItems: "center", gap: 8, padding: first ? "4px 2px 10px" : "18px 2px 10px" }}>
      {icon && <span className="row" style={{ color: "var(--text-2)", flex: "none" }}>{icon}</span>}
      <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.01em", color: "var(--text-2)" }}>{title}</span>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--text-3)", background: "var(--surface-3)", padding: "1px 8px", borderRadius: 999, lineHeight: 1.55 }}>{count}</span>
      <span style={{ flex: 1, height: 1, background: "var(--border-strong)" }} />
    </div>
  );
}

/* ---------- Dashboard ---------- */
/* 카드 레이아웃 숨김(2026-06-12) — 그리드 전용. DeviceTile 의 card 분기는 보존. */
export function DashboardScreen({ dojang, devices, loading = false, now = 0, onOpen, onToggle, onLoadDevice, onPair, onSwitchDojang, onEndAll, onPauseAll, onResumeAll, selecting = false, selIds, onExitSelecting, onSelectDevice, onLongPress, onSelectAll, onBatch, onBatchLoad, onBatchView, controlBy, camBy, onLowerHand, onVoice }: {
  dojang: DojangView;
  devices: DeviceView[];
  loading?: boolean; // 첫 목록 로드 전 — 빈 상태 플래시 방지
  now?: number; // 위치 보간용 틱
  onOpen: (d: DeviceView) => void;
  onToggle: (d: DeviceView) => void; // 타일 라벨 버튼 → 재생/일시정지
  onLoadDevice?: (d: DeviceView) => void; // 대기 타일 '영상 틀기' → 로드 시트
  onPair: () => void;
  onSwitchDojang: () => void;
  onEndAll?: () => void; // 수업 전체 종료 — 온라인 기기 전부 대기 화면으로
  onPauseAll?: () => void; // 재생 중인 기기 전부 일시정지
  onVoice?: () => void; // '말로 조작' 🎤 — 허용 계정만(owner.voice) 넘어온다
  onResumeAll?: () => void; // 일시정지된 기기 전부 다시 재생
  // 기기 다중선택(즉석) — 그때그때 고른 기기들에 일괄 명령
  selecting?: boolean;
  selIds?: Set<string>;
  onExitSelecting?: () => void;  // '취소'
  onSelectDevice?: (d: DeviceView) => void; // 탭 → 선택 토글
  onLongPress?: (d: DeviceView) => void;    // 길게 누름 → 선택 모드 진입
  onSelectAll?: () => void;
  onBatch?: (action: "PLAY" | "PAUSE" | "STOP" | "RELOAD") => void;
  onBatchView?: () => void; // 선택한 기기들 카메라를 한 창에 동시에 보기(개발 중 기능 — 미노출 시 undefined)
  onBatchLoad?: () => void; // '같은 영상 틀기' 시트 열기
  controlBy?: Record<string, string>; // 기기별 제어자 이름(관장 전용) — undefined면 미표시(부관리자)
  camBy?: Record<string, "view" | "send" | "both">; // 기기별 카메라 표식(보는 중 / 전송 중)
  onLowerHand?: (d: DeviceView) => void; // 손들기 '확인' — 든 손 내리기
}) {
  const t = useTranslations("control.dash");
  const tc = useTranslations("control.common");
  const tv = useTranslations("control.voice");
  // 이 화면은 **도장 화면만** 그린다 — 집 화면(학생 집에 둔 화면)은 홈워킹 탭이 학생 단위로 맡는다.
  // 🛑 그래도 헤더 통계와 '전체' 버튼은 여기서 다시 한 번 걸러야 한다(atDojang): 세는 것과 닿는 것이
  //    같아야 "온라인 9대"라 해 놓고 전체 종료가 3대만 끄는 화면이 안 된다(전체 조작은 집을 안 건드린다).
  // 🔑 넘겨받는 devices 에는 집 화면도 들어 있다 — 손든 학생(raisedN)은 집에서도 봐야 하기 때문이다.
  const dojangDevices = devices.filter(atDojang);
  const onlineN = dojangDevices.filter((d) => d.online).length;
  const playingN = dojangDevices.filter((d) => d.online && d.status === "playing").length;
  const pausedN = dojangDevices.filter((d) => d.online && d.status === "paused").length;
  const raisedN = devices.filter((d) => d.online && d.handRaised).length; // 손든 기기 수 — 헤더 호출 배지(집 화면 포함: 학생이 부르면 어디서든 봐야 한다)
  // 지금 화면에 떠 있는 제어자들에게 서로 먼 색을 나눠준다(이름 해시로는 금세 겹친다 — types 주석 참고).
  const dotBy = controllerDots(Object.values(controlBy ?? {}));
  const activeN = playingN + pausedN; // 수업 진행 중(재생+일시정지) 기기 수 — 전체 제어 버튼 노출 기준
  const selCount = selIds?.size ?? 0;
  // 상태별 그룹 — 입력(등록)순을 보존하는 partition(상태가 바뀌어도 타일이 재정렬로 튀지 않게).
  // 🔑 위 묶음(재생 중)만은 **재생 순서**다(2026-09-15 사용자 "재생 순서대로 위에 뜨게"): 먼저 튼 기기가 위.
  //    기준은 DeviceView.activeAt(이 앱이 수업 중 진입을 본 시각). 같은 시각(앱을 새로 열었을 때 이미
  //    재생 중이던 것들)은 안정 정렬이라 등록순 그대로. 일시정지는 자리 유지, 대기로 갔다 다시 틀면 맨 뒤.
  const activeDevices = dojangDevices
    .filter((d) => d.online && d.status !== "idle") // 재생 중 + 일시정지
    .sort((a, b) => (a.activeAt ?? 0) - (b.activeAt ?? 0));
  const waitingDevices = dojangDevices.filter((d) => d.online && d.status === "idle"); // 대기(온라인·유휴)
  const offlineDevices = dojangDevices.filter((d) => !d.online);
  // 사용법 둘러보기가 가리킬 '첫 기기' — 재생 중이 없으면 대기, 그것도 없으면 오프라인 것이라도 잡는다.
  // ⚠️ 온라인 기기에만 표식을 달면 기기가 다 꺼져 있을 때 안내가 갈 곳을 잃고 그 칸에서 갇힌다.
  const tourFirstDeviceId = (activeDevices[0] ?? waitingDevices[0] ?? offlineDevices[0])?.id;
  // 자동 시연 코스②('여러 화면에 한 번에')가 두 번째·세 번째 화면을 집으려면 순번이 있어야 한다.
  // ⚠️ `data-tour` 는 한 자리에 하나뿐이라(첫 기기는 이미 device-first) 별도 표식으로 단다.
  //    꺼진 기기는 번호를 안 준다 — 자동이 꺼진 화면을 골라 봐야 아무 일도 안 일어난다.
  const demoOrder = new Map([...activeDevices, ...waitingDevices].map((d, k) => [d.id, k + 1] as const));
  return (
    <div className="screen col" style={{ height: "100%" }}>
      {/* 플로팅 스크롤반응 헤더 — 최상단 반투명 → 내릴수록 불투명(설정·제어화면과 통일).
          헤더가 스크롤 컨테이너 '안'의 sticky 여야 ScrollSolidify 가 --hd-solid 를 먹일 수 있다. */}
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
      <div className="ctl-hd-sticky">
      <AppBar title={dojang.name}
        sub={selecting ? t("selected", { count: selCount }) : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {t("online")} <span style={{ color: "var(--text-1)", fontWeight: 700, fontSize: 13.5 }}>{onlineN}</span>
            <span style={{ color: "var(--text-3)" }}>/{dojangDevices.length}</span>
          </span>
        )}
        badge={!selecting && raisedN > 0 ? (
          <span aria-label={t("raisedHandsAria", { count: raisedN })} style={{
            display: "inline-flex", alignItems: "center", gap: 4, flex: "none",
            padding: "3px 9px", borderRadius: 999, background: "var(--warn)", color: "#1a1200",
            fontSize: 12.5, fontWeight: 800, animation: "tedu-hand-pulse 1.2s ease-in-out infinite",
          }}>✋ {raisedN}</span>
        ) : undefined}
        onBack={onSwitchDojang}
        right={
          selecting ? (
            <div className="row gap-4" style={{ alignItems: "center" }}>
              <button onClick={onSelectAll} style={selBtnStyle}>{t("selectAll")}</button>
              <button onClick={onExitSelecting} style={selBtnStyle}>{tc("cancel")}</button>
            </div>
          ) : (
            // 제어화면 상단바와 통일 — 무테 라인 클러스터(박스 없음). '선택' 버튼은 제거(다중선택은 타일 길게눌러 진입).
            <div className="row gap-6" style={{ alignItems: "center" }}>
              {onVoice && (
                <button className="iconbtn iconbtn--plain" onClick={onVoice} aria-label={tv("aria")}>
                  <I.mic size={20} />
                </button>
              )}
              {/* 전체 일시정지/재생 토글 — 항상 표시(레이아웃 고정), 진행 중인 기기가 없으면 비활성 */}
              <button className="iconbtn iconbtn--plain" disabled={activeN === 0} style={{ opacity: activeN === 0 ? 0.35 : 1 }}
                onClick={playingN > 0 ? onPauseAll : onResumeAll}
                aria-label={playingN > 0 ? t("pauseAll") : t("playAll")}>
                {playingN > 0 ? <I.pause size={20} /> : <I.play size={20} />}
              </button>
              {/* 수업 전체 종료 — 항상 표시, 진행 중인 기기가 없으면 비활성. 온라인 전부 대기 화면(GO_HOME)으로 */}
              {onEndAll && (
                <button className="iconbtn iconbtn--plain" disabled={activeN === 0} onClick={onEndAll} aria-label={t("endAll")}
                  style={{ color: "var(--danger)", opacity: activeN === 0 ? 0.35 : 1 }}><I.power size={20} /></button>
              )}
              <button className="iconbtn iconbtn--plain" onClick={onPair} aria-label={t("addDevice")}><I.plus size={20} /></button>
            </div>
          )
        } />
      </div>

      <div style={{ padding: "6px 18px 18px" }}>
        {loading ? (
          <div className="col" style={{ alignItems: "center", padding: "60px 0" }}>
            <div className="spin" style={{ color: "var(--accent)" }}><I.spinner size={28} sw={2} /></div>
          </div>
        ) : dojangDevices.length === 0 ? (
          <div className="col gap-16" style={{ alignItems: "center", padding: "44px 24px 28px" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--surface-3)", display: "grid", placeItems: "center" }}>
              <I.monitor size={32} className="dim" />
            </div>
            <div className="col gap-6" style={{ alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t("emptyTitle")}</div>
              <div className="muted" style={{ fontSize: 14, textAlign: "center", lineHeight: 1.5 }}>{t("emptyDesc")}</div>
            </div>
            <button className="btn btn--primary" style={{ height: 50, padding: "0 26px", marginTop: 4, width: "auto" }} onClick={onPair}><I.qr size={18} />{t("registerDevice")}</button>
          </div>
        ) : (
          <>
            {/* 우선순위는 '크기'가 아니라 순서·그룹·흐림으로만 — 타일 크기는 세 그룹 모두 동일(균일 그리드) */}
            {/* 재생 중 — 가장 먼저(시선 우선) */}
            {activeDevices.length > 0 && (
              <>
                <GroupHead title={t("groupPlaying")} count={activeDevices.length} first />
                <div className="stagger dash-grid">
                  {/* data-tour — 사용법 둘러보기가 '첫 번째 기기'를 잡는 표식(components/tour.tsx) */}
                  {activeDevices.map((d, i) => <div key={d.id} data-tour={d.id === tourFirstDeviceId ? "device-first" : undefined} data-tour-dev={demoOrder.get(d.id)} style={{ animationDelay: `${i * 45}ms`, minWidth: 0 }}><DeviceTile device={d} layout="grid" now={now} onOpen={onOpen} onToggle={onToggle} onLoadDevice={onLoadDevice} selecting={selecting} selected={selIds?.has(d.id) ?? false} onSelect={onSelectDevice} onLongPress={onLongPress} controlledBy={controlBy?.[d.id]} controlledDot={dotBy[controlBy?.[d.id] ?? ""]} cam={camBy?.[d.id]} onLowerHand={onLowerHand} /></div>)}
                </div>
              </>
            )}
            {/* 대기 — 동일 크기 타일 */}
            {waitingDevices.length > 0 && (
              <>
                <GroupHead title={t("groupWaiting")} count={waitingDevices.length} first={activeDevices.length === 0} />
                <div className="dash-grid">
                  {waitingDevices.map((d) => <div key={d.id} data-tour={d.id === tourFirstDeviceId ? "device-first" : undefined} data-tour-dev={demoOrder.get(d.id)} style={{ minWidth: 0 }}><DeviceTile device={d} layout="grid" now={now} onOpen={onOpen} onToggle={onToggle} onLoadDevice={onLoadDevice} selecting={selecting} selected={selIds?.has(d.id) ?? false} onSelect={onSelectDevice} onLongPress={onLongPress} controlledBy={controlBy?.[d.id]} controlledDot={dotBy[controlBy?.[d.id] ?? ""]} cam={camBy?.[d.id]} onLowerHand={onLowerHand} /></div>)}
                </div>
              </>
            )}
            {/* 오프라인 — 크기는 동일하되 타일 자체 흐림(opacity)으로 한 단계 낮춤 */}
            {offlineDevices.length > 0 && (
              <>
                <GroupHead title={t("groupOffline")} count={offlineDevices.length} />
                <div className="dash-grid">
                  {offlineDevices.map((d) => <div key={d.id} data-tour={d.id === tourFirstDeviceId ? "device-first" : undefined} data-tour-dev={demoOrder.get(d.id)} style={{ minWidth: 0 }}><DeviceTile device={d} layout="grid" now={now} onOpen={onOpen} onToggle={onToggle} onLoadDevice={onLoadDevice} selecting={selecting} selected={selIds?.has(d.id) ?? false} onSelect={onSelectDevice} onLongPress={onLongPress} controlledBy={controlBy?.[d.id]} controlledDot={dotBy[controlBy?.[d.id] ?? ""]} cam={camBy?.[d.id]} onLowerHand={onLowerHand} /></div>)}
                </div>
              </>
            )}
            {/* 🛑 집 화면(학생 집에 둔 화면)은 여기 안 그린다 — 홈워킹 탭이 학생 단위로 맡는다.
                도장에서 훑는 화면에 남의 집 거실이 섞일 자리가 아니다(2026-09-02 사용자 결정). */}
            {/* 새 디바이스 등록 — 맨 아래 가로 바. 폰맹 관장도 "여기서 추가하는 자리"로 알아보게
                점선 테두리 + 반투명 옅은 파랑(빈 화면 업로드 카드와 같은 톤)으로 눈에 띄게. */}
            <button className="btn btn--block" onClick={onPair} style={{
              height: 44, marginTop: 14, fontSize: 14, gap: 6, fontWeight: 700,
              background: "var(--accent-soft)", border: "1px dashed var(--accent-line)", color: "var(--accent-ink)",
            }}>
              <I.qr size={16} />{t("registerNewDevice")}
            </button>
          </>
        )}
      </div>
      </ScrollSolidify>
      {/* 선택 모드 하단 바 — 고른 기기에 일괄 재생/일시정지/정지·같은 영상 틀기.
          스크롤 컨테이너 '밖' — 스크롤과 무관하게 바닥에 붙어 있어야 한다.
          🛑 **좁은 칸에서도 안 삐져나와야** 한다. 가로 2단(태블릿·PC)에서 왼쪽 칸이 360px 인데,
             예전엔 안내글을 flex:none 으로 두고 조작 묶음에 flex:1 을 줘서 **넘치는 만큼 오른쪽으로
             밀려 나갔다**(버튼 오른쪽이 353px — 여백 16px 을 삼키고 칸 경계에 붙었다. 2026-08-31 제보).
             이제 줄어드는 쪽은 **안내글**이고(말줄임), 조작 묶음은 제 크기를 지킨다.
             overflow:hidden 은 그래도 남는 1~2px 이 칸 밖으로 새는 걸 막는 마지막 빗장이다. */}
      {selecting && (
        <div className="row" style={{
          flex: "none", alignItems: "center", gap: 6, overflow: "hidden",
          padding: "10px 12px", paddingBottom: "max(10px, env(safe-area-inset-bottom))",
          borderTop: "1px solid var(--border)", background: "var(--surface-1)",
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, flex: "1 1 auto", minWidth: 0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {selCount > 0 ? t("selected", { count: selCount }) : t("selectDevices")}
          </span>
          {/* 아이콘 사이 간격을 4로 좁힌다 — 영문("1 selected")은 한글("1대 선택")보다 길어
              6일 때 왼쪽 글자가 3px 모자라 잘렸다(2026-08-31 실측). 탭 영역(40px)은 그대로다. */}
          <div className="row gap-4" style={{ flex: "0 0 auto", justifyContent: "flex-end", alignItems: "center" }}>
            {/* 상단바와 통일 — 무테 라인. 주 액션(같은 영상 틀기)만 파란 채움으로 위계 유지. */}
            {/* 여러 기기 카메라를 한 창에 동시에 — 개발 중 기능이라 미노출 시 undefined(운영에선 안 보인다) */}
            {onBatchView && (
              <button className="iconbtn iconbtn--plain" disabled={selCount === 0} style={{ opacity: selCount === 0 ? 0.35 : 1 }}
                onClick={onBatchView} aria-label={t("viewLiveAria")}><I.video size={20} /></button>
            )}
            {/* data-tour — 자동 시연 코스②가 짚고 누르는 자리. 지우지 말 것. */}
            <button data-tour="batch-play" className="iconbtn iconbtn--plain" disabled={selCount === 0} style={{ opacity: selCount === 0 ? 0.35 : 1 }}
              onClick={() => onBatch?.("PLAY")} aria-label={t("playSelAria")}><I.play size={20} /></button>
            <button data-tour="batch-pause" className="iconbtn iconbtn--plain" disabled={selCount === 0} style={{ opacity: selCount === 0 ? 0.35 : 1 }}
              onClick={() => onBatch?.("PAUSE")} aria-label={t("pauseSelAria")}><I.pause size={20} /></button>
            {/* 🛑 라벨은 **고른 뒤에만** 단다. 아직 안 골랐을 땐 왼쪽에 "기기를 선택하세요"라는 긴 글이
                떠 있어, 여기까지 글자를 달면 좁은 칸(2단 왼쪽 360px)에서 줄이 넘쳐 안내글이 "기기 …"로
                잘린다(2026-08-31 실측). 고르고 나면 그 자리가 "N대 선택"으로 짧아져 라벨이 들어간다.
                어차피 안 골랐을 땐 비활성이라 이름을 읽을 일도 없다 — 이름은 aria-label 로 남는다. */}
            <button data-tour="batch-load" className="btn btn--primary" disabled={selCount === 0}
              aria-label={t("sameVideo")} title={t("sameVideo")}
              style={{ height: 42, width: selCount === 0 ? 46 : "auto", flex: "none", padding: selCount === 0 ? 0 : "0 12px", opacity: selCount === 0 ? 0.4 : 1 }}
              onClick={onBatchLoad}><I.cast size={selCount === 0 ? 19 : 16} />{selCount > 0 && t("sameVideo")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// 숙제를 끝낸 때 → 목록에 쓸 조각. '오늘/어제/N일 전'은 시간 차가 아니라 **날이 바뀐 횟수**로 센다
// (밤 11시에 끝낸 숙제를 다음 날 아침에 '10시간 전'이 아니라 '어제'로 읽는다). 이레가 넘으면 날짜로.
// 🔑 지금 시각 읽기는 이 함수 안에 가둔다 — 렌더 본문에서 Date.now() 를 부르면 리액트 컴파일러가 막는다.
// 🔑 '그 날'은 **도장 표준시**로 자른다 — 관장이 해외에 있어도 하루의 경계는 도장의 것이다.
function homeworkWhen(iso: string, timeZone?: string): { kind: "today" | "yesterday" | "date" } | { kind: "days"; days: number } {
  const f = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone });
  const day = (ms: number) => Date.parse(f.format(new Date(ms)));
  const days = Math.round((day(Date.now()) - day(Date.parse(iso))) / 86400000);
  if (days <= 0) return { kind: "today" };
  if (days === 1) return { kind: "yesterday" };
  if (days < 7) return { kind: "days", days };
  return { kind: "date" };
}

/* ---------- 홈워킹 — 학생 집에 둔 화면을 '학생' 단위로 본다 ----------
   🔑 기기 탭과 하는 일이 다르다: 저쪽은 지금 도장에서 무엇이 돌아가나(수업), 이쪽은 누가 숙제를 했나(집).
      그래서 타일·미리보기가 아니라 이름 목록이다 — 관장이 여기서 찾는 건 화면이 아니라 아이다.
   🛑 한 학생이 폰과 PC를 다 등록해도 **한 줄**이다(Student 하나로 묶임). 숙제는 학생에게 내는 것이라,
      기기별로 줄이 늘면 같은 아이에게 두 번 내는 실수가 생긴다. */
export function HomeworkScreen({ devices, timeZone, onAssign, onOpen, onPair, onSelecting, linkStudents = [], onOpenLinkStudent, classes = [], onMoveClass, onOpenClasses }: {
  devices: DeviceView[];
  timeZone?: string;      // 도장 표준시 — '오늘/어제'를 도장의 하루로 가른다
  onAssign: (devices: DeviceView[]) => void; // 고른 학생들에게 숙제 내기 시트
  onOpen: (device: DeviceView) => void;      // 학생 줄 탭 → 그 학생 화면의 ⋯ 메뉴(숙제·범위·이름·등록해제)
  onPair: (classId: string | null) => void;  // 수련생 등록 — 반을 고른 채 누르면 그 반 id 가 같이 간다(등록 화면 반 칸이 미리 채워진다)
  // 🛑 고르는 중이라고 **페이지에 알린다** — 안 알리면 탭바가 그대로 떠서 하단 선택 줄을 덮는다
  //    (기기 탭도 고르는 동안 탭바를 감춘다. 이 화면만 상태를 혼자 들고 있어 페이지가 몰랐다).
  onSelecting?: (on: boolean) => void;
  /** 링크는 만들었지만 아직 안 연 수련생(기기 0대) — 기기 목록엔 없어 따로 받는다(2026-09-10, StudentView). */
  linkStudents?: StudentView[];
  onOpenLinkStudent?: (s: StudentView) => void; // 그 줄 탭 → 링크 복사·공유·새로 만들기 시트
  /** 반 목록(2026-10-07). 🛑 비어 있으면 반 칩·'반 옮기기'를 **아예 안 그린다** — 반을 안 나누는 도장은 전과 똑같아야 한다. */
  classes?: ClassView[];
  onMoveClass?: (devices: DeviceView[]) => void; // 고른 수련생들을 다른 반으로(한 명을 옮길 때도 이 길 하나)
  /** 반이 하나도 없을 때 목록 아래 안내 한 줄('반 나누기')이 가는 곳 — 반을 어디서 만드는지 이 탭만 봐선 알 수 없었다(10-07 사용자). */
  onOpenClasses?: () => void;
}) {
  const t = useTranslations("control.homework");
  const tc = useTranslations("control.common");
  const locale = useLocale();
  const { plById } = useData();
  const [filter, setFilter] = useState<"all" | "done" | "notYet">("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [clsPick, setClsPick] = useState<string>("all"); // "all" | "none" | 반 id

  // 같은 학생의 폰·PC 를 한 줄로 접는다(studentId 기준, 없으면 기기 자체가 한 학생).
  // 대표 기기 하나만 건드려도 서버가 그 학생의 기기 전부에 같은 숙제를 실어 준다.
  const allStudents = Array.from(new Map(devices.filter((d) => !atDojang(d)).map((d) => [d.studentId ?? d.id, d])).values())
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  const deviceCount = (s: DeviceView) =>
    s.studentId ? devices.filter((d) => !atDojang(d) && d.studentId === s.studentId).length : 1;
  // 링크만 있고 기기가 없는 수련생 — '전체' 필터에서만, 기기 줄 뒤에 붙인다(완료/미완료는 기기가 있어야 뜻이 있다).
  const allPending = linkStudents.filter((s) => s.deviceCount === 0 && !allStudents.some((d) => d.studentId === s.id))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  // ---- 반 ---- 반은 수련생(Student)에 붙는다. 기기 줄은 studentId 로 수련생 목록에서 반을 찾는다.
  //   지워진 반 id 가 남아 있으면 '반 없음'으로 본다(서버도 SetNull 이지만 목록이 한 박자 늦을 수 있다).
  const hasClasses = classes.length > 0;
  const classIdOf = new Map(linkStudents.map((s) => [s.id, s.classId && classes.some((c) => c.id === s.classId) ? s.classId : null]));
  const clsOfDev = (d: DeviceView) => (d.studentId ? classIdOf.get(d.studentId) ?? null : null);
  const clsOfSt = (s: StudentView) => classIdOf.get(s.id) ?? null;
  const countIn = (cid: string | null) => allStudents.filter((d) => clsOfDev(d) === cid).length + allPending.filter((s) => clsOfSt(s) === cid).length;
  const noneCount = hasClasses ? countIn(null) : 0;
  // 🔑 '반 없음'을 보다가 모두 옮기면 칩이 사라지고 **'전체'로 돌아온다** — 안 그러면 '반 없음 0 · 수련생이 없습니다'
  //    빈 화면에 갇힌다(10-07 점검: 방금 옮긴 아이들이 어디 갔는지 안 보였다).
  const cls = hasClasses && ((clsPick === "none" && noneCount > 0) || classes.some((c) => c.id === clsPick)) ? clsPick : "all";
  const inCls = (cid: string | null) => cls === "all" || (cls === "none" ? cid === null : cid === cls);
  const students = allStudents.filter((d) => inCls(clsOfDev(d)));
  const pending = allPending.filter((s) => inCls(clsOfSt(s)));
  const allTotal = allStudents.length + allPending.length; // 탭이 통째로 비었나(빈 화면)·상단 버튼은 반과 무관하게 이 수로
  const total = students.length + pending.length;
  const doneList = students.filter((d) => d.homePlaylistId && d.homeworkDoneAt);
  const notYetList = students.filter((d) => d.homePlaylistId && !d.homeworkDoneAt);
  const shown = filter === "done" ? doneList : filter === "notYet" ? notYetList : students;
  const picked = students.filter((d) => selected.has(d.id));

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // 반(또는 '반 없음')을 고른 채 '선택'을 누르면 **그 반 전체가 골라진 채** 시작한다 — 뺄 아이만 체크를 푼다.
  const enterSelect = () => { setSelecting(true); onSelecting?.(true); if (cls !== "all") setSelected(new Set(students.map((x) => x.id))); };
  const exitSelect = () => { setSelecting(false); setSelected(new Set()); onSelecting?.(false); };
  // 이 화면을 떠날 때(탭 전환·도장 바꾸기) 표식을 반드시 내린다 — 안 그러면 탭바가 영영 안 돌아온다.
  useEffect(() => () => onSelecting?.(false), [onSelecting]);
  // 길게 눌러 선택 — 기기 탭 타일과 같은 손짓이다(거기서 배운 손이 여기서도 통해야 한다).
  const longPress = (s: DeviceView) => { setSelecting(true); onSelecting?.(true); setSelected(new Set([s.id])); };
  const pair = () => onPair(cls === "all" || cls === "none" ? null : cls);
  const showMove = hasClasses && !!onMoveClass;
  const tagOf = (cid: string | null) => (cls === "all" && cid ? classes.find((c) => c.id === cid)?.name ?? null : null);

  const whenText = (iso: string) => {
    const w = homeworkWhen(iso, timeZone);
    return w.kind === "today" ? t("doneToday")
      : w.kind === "yesterday" ? t("doneYesterday")
        : w.kind === "days" ? t("doneDaysAgo", { days: w.days })
          : new Intl.DateTimeFormat(locale, { month: "numeric", day: "numeric", timeZone }).format(new Date(iso));
  };
  // 라이브러리·부관리자 화면의 빈 칸과 같은 모양(아이콘 → 한 줄). 목록 안에서 잠깐 비는 자리라 크게 안 만든다.
  const emptyBox = (icon: ReactNode, text: string) => (
    <div className="col gap-10" style={{ alignItems: "center", padding: "28px 20px", color: "var(--text-3)" }}>
      {icon}<span style={{ fontSize: 14 }}>{text}</span>
    </div>
  );

  return (
    <div className="screen col" style={{ height: "100%" }}>
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
        <div className="ctl-hd-sticky"><AppBar
          title={t("title")}
          sub={t("subtitle", { count: total })}
          right={allTotal > 0 && (
            // 선택 중 상단바 = [전체 선택][취소] — 기기 탭과 같은 자리·같은 글자 버튼.
            selecting ? (
              <div className="row gap-4" style={{ alignItems: "center" }}>
                <button onClick={() => setSelected(new Set(students.map((x) => x.id)))} style={selBtnStyle}>{t("selectAll")}</button>
                <button onClick={exitSelect} style={selBtnStyle}>{tc("cancel")}</button>
              </div>
            ) : (
              // 🔑 기기 탭은 상단바가 이미 아이콘 다섯이라 '선택'을 뺐지만(길게눌러 진입), 여기는 그 자리가
              //    비어 있고 **여러 명에게 한 번에 내는 것이 이 화면의 본업**이라 문을 보이게 둔다.
              // ＋ = 집 화면(학생) 등록 — 기기 탭의 ＋ 와 같은 자리·같은 모양. 전엔 빈 화면에만 등록 버튼이 있어
              //    학생이 한 명 생기면 다음 학생을 등록할 길이 이 탭에서 사라졌다(2026-09-04 사용자 지적).
              <div className="row gap-2" style={{ alignItems: "center" }}>
                <button onClick={enterSelect} style={selBtnStyle}>{t("select")}</button>
                <button className="iconbtn iconbtn--plain" onClick={pair} aria-label={t("createLink")}><I.plus size={20} /></button>
              </div>
            )
          )}
        /></div>

        {/* 반 칩 — 완료·미완료 탭 **위**. 반을 하나도 안 만든 도장엔 없다. '반 없음'은 반 없는 수련생이 있을 때만. */}
        {hasClasses && allTotal > 0 && (
          <div className="row gap-6" style={{ overflowX: "auto", padding: "4px 18px 10px", scrollbarWidth: "none" }}>
            {[{ id: "all", label: `${t("filterAll")} ${allTotal}` },
              ...(noneCount > 0 ? [{ id: "none", label: `${t("classNone")} ${noneCount}` }] : []),
              ...classes.map((c) => ({ id: c.id, label: `${c.name} ${countIn(c.id)}` })),
            ].map((c) => {
              const on = cls === c.id;
              return (
                <button key={c.id} onClick={() => { setClsPick(c.id); if (selecting) setSelected(new Set()); }} aria-pressed={on} style={{
                  flex: "none", height: 36, padding: "0 14px", borderRadius: 999, fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", cursor: "pointer",
                  background: on ? "var(--accent)" : "var(--surface-1)", color: on ? "#fff" : "var(--text-1)",
                  border: "1px solid " + (on ? "var(--accent)" : "var(--border)"),
                }}>{c.label}</button>
              );
            })}
          </div>
        )}

        {allTotal > 0 && (
          <div style={{ padding: "0 18px 10px" }}>
            <Segmented value={filter} onChange={setFilter} options={[
              { value: "all", label: `${t("filterAll")} ${total}` },
              { value: "done", label: `${t("filterDone")} ${doneList.length}`, tone: "live" },
              { value: "notYet", label: `${t("filterNotYet")} ${notYetList.length}`, tone: "warn" },
            ]} />
          </div>
        )}

        {allTotal === 0 ? (
          /* 이 탭이 통째로 비었을 때 — 기기 탭 빈 화면과 같은 얼개(둥근 아이콘 → 제목 → 설명 → 등록) */
          <div className="col gap-16" style={{ alignItems: "center", padding: "44px 24px 28px" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--surface-3)", display: "grid", placeItems: "center" }}>
              <I.home size={32} className="dim" />
            </div>
            <div className="col gap-6" style={{ alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{t("emptyTitle")}</div>
              <div className="muted" style={{ fontSize: 14, textAlign: "center", lineHeight: 1.5 }}>{t("emptyDesc")}</div>
            </div>
            <button className="btn btn--primary" style={{ height: 50, padding: "0 26px", marginTop: 4, width: "auto" }} onClick={pair}>
              <I.link size={18} />{t("createLink")}
            </button>
          </div>
        ) : (
          <div key={filter + cls} className="slide-in col gap-10" style={{ padding: "4px 18px 18px" }}>
            <SecLabel>{t("listLabel")}</SecLabel>
            {shown.length === 0 && !(filter === "all" && pending.length > 0)
              ? emptyBox(
                  filter === "done" ? <I.clock size={30} /> : <I.check size={30} />,
                  filter === "done" ? t("emptyDone") : filter === "notYet" ? t("emptyNotYet") : t("empty"),
                )
              : (
                <GlassGroup glass inset={71}>
                  {shown.map((s) => (
                    <StudentRow
                      key={s.studentId ?? s.id}
                      student={s}
                      playlistName={s.homePlaylistId ? (plById[s.homePlaylistId]?.name ?? t("unknownPlaylist")) : null}
                      // 분모는 **서버가 '다 했다'를 판정할 때 쓰는 수**와 같아야 한다(중복 영상은 한 편으로).
                      // 어긋나면 5/6 에서 멈춘 것처럼 보이는데 배지는 '했어요'로 켜진다.
                      total={s.homePlaylistId ? new Set(plById[s.homePlaylistId]?.items ?? []).size : 0}
                      devices={deviceCount(s)}
                      doneText={s.homeworkDoneAt ? whenText(s.homeworkDoneAt) : null}
                      selecting={selecting}
                      selected={selected.has(s.id)}
                      onTap={() => (selecting ? toggle(s.id) : onOpen(s))}
                      onLongPress={() => longPress(s)}
                      classTag={tagOf(clsOfDev(s))}
                    />
                  ))}
                  {/* 링크를 아직 안 연 수련생 — 고를 수 없다(자기주도 수련은 기기에 싣는다). 회색 칩이 '보내라'고 말한다. */}
                  {filter === "all" && !selecting && pending.map((s) => (
                    <GlassRow
                      key={"pending-" + s.id}
                      lead={<div style={{ width: 40, height: 40, borderRadius: "50%", flex: "none", background: "var(--surface-3)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 16, color: "var(--text-3)" }}>{s.name.trim()[0] ?? "?"}</div>}
                      title={nameWithClass(s.name, tagOf(clsOfSt(s)))}
                      sub={<span className="muted" style={{ fontSize: 12.5 }}>{t("linkPending")}</span>}
                      trailing={<div className="row gap-6" style={{ alignItems: "center", flex: "none" }}><span className="chip chip--off">{t("linkPendingChip")}</span><I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} /></div>}
                      onClick={onOpenLinkStudent ? () => onOpenLinkStudent(s) : undefined}
                    />
                  ))}
                </GlassGroup>
              )}
            {/* 집 화면 등록 — 맨 아래 가로 바. 기기 탭 맨 아래 '새 디바이스 등록'과 같은 모양(점선+옅은 파랑). */}
            {!selecting && (
              <button className="btn btn--block" onClick={pair} style={{
                height: 44, marginTop: 4, fontSize: 14, gap: 6, fontWeight: 700,
                background: "var(--accent-soft)", border: "1px dashed var(--accent-line)", color: "var(--accent-ink)",
              }}>
                <I.link size={16} />{t("createLink")}
              </button>
            )}
            {/* 반을 아직 안 만든 도장에만 — 조용한 한 줄. 반을 만들면 사라지고 위에 반 칩이 선다. */}
            {!selecting && !hasClasses && onOpenClasses && (
              <button onClick={onOpenClasses} style={{ background: "none", border: "none", padding: "6px 0 0", fontSize: 12.5, color: "var(--text-3)", cursor: "pointer", textAlign: "center" }}>
                {t("classHint")} <span style={{ color: "var(--accent-ink)", fontWeight: 700 }}>{t("classHintLink")} ›</span>
              </button>
            )}
          </div>
        )}
      </ScrollSolidify>

      {/* 선택 줄 — 기기 탭 선택 줄과 같은 톤(스크롤 밖·바닥 고정·안내글이 먼저 줄어든다) */}
      {selecting && (
        // 🔑 버튼이 둘(반 옮기기·지정)일 때는 **줄바꿈을 허용**한다 — 한국어는 한 줄에 서지만 영문('Assign self-directed
        //    training')은 안 들어간다. 그때만 인원 글이 윗줄, 버튼 둘이 아랫줄로 내려간다(2026-10-07 시안 ⑦ 확정).
        <div className="row" style={{
          flex: "none", alignItems: "center", gap: 6, overflow: "hidden", flexWrap: showMove ? "wrap" : "nowrap", rowGap: 8,
          padding: "10px 12px", paddingBottom: "max(10px, env(safe-area-inset-bottom))",
          borderTop: "1px solid var(--border)", background: "var(--surface-1)",
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, flex: showMove ? "1 0 auto" : "1 1 auto", minWidth: 0,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {picked.length > 0 ? t("selected", { count: picked.length }) : t("selectStudents")}
          </span>
          <div className="row" style={{ gap: 6, flex: showMove ? "0 1 auto" : "none", minWidth: 0, maxWidth: "100%", marginLeft: "auto" }}>
            {showMove && (
              <button className="btn btn--ghost" disabled={picked.length === 0}
                style={{ height: 42, flex: "none", padding: "0 11px", fontSize: 14, whiteSpace: "nowrap", opacity: picked.length === 0 ? 0.4 : 1 }}
                onClick={() => { onMoveClass?.(picked); exitSelect(); }}>
                {t("moveClass")}
              </button>
            )}
            <button className="btn btn--primary" disabled={picked.length === 0}
              style={{ height: 42, flex: showMove ? "0 1 auto" : "none", minWidth: 0, padding: showMove ? "0 11px" : "0 12px", ...(showMove ? { fontSize: 14 } : null), whiteSpace: "nowrap", overflow: "hidden", opacity: picked.length === 0 ? 0.4 : 1 }}
              onClick={() => { onAssign(picked); exitSelect(); }}>
              {/* 버튼이 둘이면 아이콘을 뺀다 — 영문 글('Assign self-directed training')이 360px 폰에서 그만큼 모자란다. */}
              {!showMove && <I.list size={16} style={{ flex: "none" }} />}<span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("assign")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* 학생 한 줄 — 부관리자 목록(MemberRow)과 같은 얼개: 40px 리딩 + 이름 + 보조줄 + 우측 상태.
   훅(길게누르기)을 줄마다 걸어야 해서 별도 컴포넌트로 뺀다. */
/* 이름 옆 반 꼬리표 — '전체'를 볼 때만 붙는다(반 칩을 골랐으면 다 같은 반이라 군더더기). 아래 보조줄은 건드리지 않는다
   — 거기에 반 이름을 넣으면 폰에서 줄이 길어져 진행률(2/6)이 잘린다(09-15 기기 수 표기 때 같은 문제). */
function nameWithClass(name: string, classTag?: string | null): ReactNode {
  if (!classTag) return name;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: "0 1 auto", minWidth: 0 }}>{name}</span>
      <span style={{ flex: "0 1 auto", minWidth: 28, maxWidth: "50%", overflow: "hidden", textOverflow: "ellipsis", fontSize: 11.5, fontWeight: 700, letterSpacing: 0, color: "var(--text-2)", background: "var(--surface-3)", borderRadius: 999, padding: "2px 8px" }}>{classTag}</span>
    </span>
  );
}

function StudentRow({ student, playlistName, total, devices, doneText, selecting, selected, onTap, onLongPress, classTag }: {
  classTag?: string | null;
  student: DeviceView; playlistName: string | null; total: number; devices: number; doneText: string | null;
  selecting: boolean; selected: boolean; onTap: () => void; onLongPress: () => void;
}) {
  const t = useTranslations("control.homework");
  const lp = useLongPress(onLongPress);
  const done = Boolean(student.homeworkDoneAt);
  // 🛑 진행(3/5)과 끝낸 때(오늘)를 **같이 적지 않는다**. 다 한 줄에 '3/5'가 남아 있으면 관장이
  //    "다섯 중 셋만 봤는데 왜 했어요지?"에서 한 번 멈춘다 — 끝난 줄의 분수는 늘 5/5라 말할 값이 없고,
  //    그 자리에 정말 궁금한 건 '언제'다. 아직인 줄은 반대로 '얼마나'가 궁금하다.
  const sub = playlistName
    ? [`${t("homeworkLabel")} · ${playlistName}`,
       done ? doneText : (total ? `${Math.min(student.homeworkCompleted, total)}/${total}` : null)]
      .filter(Boolean).join(" · ")
    : t("none");
  return (
    <div onPointerDown={lp.onPointerDown} onPointerMove={lp.onPointerMove} onPointerUp={lp.onPointerUp}
      onPointerCancel={lp.cancel} onPointerLeave={lp.cancel}>
      <GlassRow
        lead={selecting
          // 고르는 중엔 아바타 자리를 체크로 바꾼다 — 동그라미 둘이 나란히 서면 어느 쪽이 선택인지 헷갈린다.
          //    🛑 아바타 자리(40px)를 통째로 파란 원으로 채우면 거대하다(09-03 제보) — 자리는 40 으로 두고 원만 24.
          ? <div style={{ width: 40, height: 40, flex: "none", display: "grid", placeItems: "center" }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center",
                background: selected ? "var(--accent)" : "transparent",
                border: "2px solid " + (selected ? "var(--accent)" : "var(--border-strong)"),
              }}>{selected && <I.check size={13} sw={3} style={{ color: "#fff" }} />}</div>
            </div>
          : <div style={{ width: 40, height: 40, borderRadius: "50%", flex: "none", background: "var(--surface-3)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 16, color: "var(--text-1)" }}>
              {student.name.trim()[0] ?? "?"}
            </div>}
        title={nameWithClass(student.name, classTag)}
        active={selecting && selected}
        sub={
          <span className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {/* 기기 수는 상한(HOME_LINK_MAX_DEVICES)에 닿았을 때만 — 평소엔 이 줄에서 할 행동이 없고 부제만 길어져
                폰에서 숙제·진행률이 잘렸다(09-15 사용자 "띄워둘 필요가 있어?" → "상한일 때만"). 기기 목록은 수련생 시트에서. */}
            {sub}{devices >= HOME_LINK_MAX_DEVICES ? ` · ${t("deviceCountMax", { count: devices })}` : ""}
          </span>
        }
        trailing={
          <div className="row gap-6" style={{ alignItems: "center", flex: "none" }}>
            {playlistName && (done
              ? <span className="chip chip--live"><I.check size={12} sw={3} />{t("done")}</span>
              // 안 했어요는 주황 — 관장이 이 화면에서 찾는 건 '안 한 아이'라 그쪽이 튀어야 한다(회색이면 제일 안 보인다).
              : <span className="chip chip--warn">{t("notYet")}</span>)}
            {!selecting && <I.chevR size={18} style={{ color: "var(--text-3)", flex: "none" }} />}
          </div>
        }
        onClick={() => { if (!lp.consumeClick()) onTap(); }}
      />
    </div>
  );
}

/* ---------- Settings ---------- */
// 투명 행 — 카드는 GlassGroup 이 담당. 행 자체는 배경/보더/그림자 없이 섹션 카드 안에서 헤어라인으로 구분.
function SettingsRow({ icon, label, value, danger, onClick }: { icon: React.ReactNode; label: string; value?: string; danger?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="row gap-12" style={{
      width: "100%", textAlign: "left", background: "transparent", border: "none",
      padding: "15px 16px", cursor: onClick ? "pointer" : "default",
      color: danger ? "var(--danger)" : "var(--text-1)",
    }}>
      {icon}<span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em", flex: 1 }}>{label}</span>
      {value && <span className="muted" style={{ fontSize: 13.5 }}>{value}</span>}
      {onClick && <I.chevR size={18} className="dim" />}
    </button>
  );
}

/* 브라우저 탭으로 연 관리 앱의 '전체 화면' — 주소창·탭줄을 숨긴다(2026-10-12 "관리 앱에도 있으면 좋겠다" 의견).
 * 수련 화면(app/player/page.tsx useTabFullscreen)과 같은 조건: **숨길 테두리가 있는 곳에서만** 줄이 뜬다.
 * 홈 화면 앱·APK(standalone)는 이미 꽉 차 있어 빠지고, 아이폰 사파리는 문서 전체 화면 API 가 없어 저절로 빠진다.
 * 저장하지 않는다 — 새로고침하면 전체 화면이 풀리는 건 브라우저 규칙이고, 다시 들어가려면 손짓이 있어야 한다. */
function useAppFullscreen(): { available: boolean; on: boolean; toggle: () => void } {
  const [available, setAvailable] = useState(false);
  const [on, setOn] = useState(false);
  const giveUpTimer = useRef(0);
  useEffect(() => {
    type FsDoc = Document & { webkitFullscreenElement?: Element | null; webkitFullscreenEnabled?: boolean };
    const doc = document as FsDoc;
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
    const api = !!(el.requestFullscreen || el.webkitRequestFullscreen) && (doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? true);
    const standalone = window.matchMedia?.("(display-mode: standalone), (display-mode: fullscreen)").matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (!api || standalone) return;
    setAvailable(true);
    const sync = () => {
      const now = !!(doc.fullscreenElement ?? doc.webkitFullscreenElement);
      if (now) window.clearTimeout(giveUpTimer.current); // 들어갔다 — 곧바로 다시 꺼도 '안 들어갔다'로 읽지 않게
      setOn(now);
    };
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);
  const toggle = () => {
    const doc = document as Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
    const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
    if (doc.fullscreenElement ?? doc.webkitFullscreenElement) {
      if (doc.exitFullscreen) doc.exitFullscreen().catch(() => {});
      else doc.webkitExitFullscreen?.();
      return;
    }
    // API 가 있다고 해 놓고 눌러도 아무 일 없는 브라우저가 있다(10-06 LG 스탠바이미) — 안 들어가면 헛줄이므로 거둔다.
    const giveUp = () => { if (!(doc.fullscreenElement ?? doc.webkitFullscreenElement)) setAvailable(false); };
    if (el.requestFullscreen) el.requestFullscreen({ navigationUI: "hide" }).catch(giveUp);
    else el.webkitRequestFullscreen?.();
    window.clearTimeout(giveUpTimer.current);
    giveUpTimer.current = window.setTimeout(giveUp, 1500);
  };
  return { available, on, toggle };
}

export function SettingsScreen({ dojang, owner, theme, onToggleTheme, onToggleTitleStay, onToggleCaptions, onCaptionLang, onSwitchDojang, onRenameDojang, onDeleteDojang, onOpenTraining, onOpenHomeDevices, onOpenMembers, onGoHome, onOpenAccount, onStartTour, demoCourses, onStartDemo }: {
  dojang: DojangView;
  owner: Owner;
  theme: "dark" | "light";
  onToggleTheme: () => void;
  onToggleTitleStay: () => void; // '영상 제목 계속 표시' — 수련생 화면 지금판을 재생 내내 / 잠깐
  onToggleCaptions?: () => void; // 동작 자막(학생 화면·미러) on/off — 도장 단위(09-16 스위치 되살림)
  onCaptionLang?: (lang: "en" | "de") => void; // 자막 언어 — 영문(기본) / 독일어(10-02)
  onSwitchDojang: () => void;
  onRenameDojang: () => void;
  onDeleteDojang: () => void;
  onOpenTraining: () => void; // 수련 기록(수업 단위 시작/종료/총시간) 화면 열기
  onOpenHomeDevices?: () => void; // 학생과 집 화면(등록된 집 화면 목록) — 집 화면 기능이 열린 계정에만 넘어온다
  onOpenMembers: () => void; // 부관리자(사범) 관리 화면 열기 — 관장 전용
  onGoHome: () => void; // 랜딩(메인 페이지)으로 나가기 — 현재 탭
  onOpenAccount: () => void; // 내 계정(이름·이메일·연락처·로그아웃·회원 탈퇴)
  onStartTour?: () => void; // 사용법 둘러보기 시작(누를 때마다 처음 칸부터). 전체 공개(킬스위치로 좁히면 안 넘어와 행도 안 뜬다)
  demoCourses?: DemoCourse[]; // 자동 시연 코스 목록(박람회). 🛑 둘러보기와 다른 플래그 — 데모·테스트 계정에만 넘어온다
  onStartDemo?: (c: DemoCourse) => void; // 코스 하나를 자동으로 돌리기
}) {
  const t = useTranslations("control.settings");
  const tr = useTranslations("control.role");
  const tt = useTranslations("control.tour");
  const canManage = dojang.role === "OWNER" || dojang.role === "SUPERADMIN";
  const Row = SettingsRow;
  const fs = useAppFullscreen();
  // 화면 언어 — 첫 방문은 브라우저 맨 앞 언어로 정해지지만(lib/locale-pick.ts), 영어 우선 브라우저를 쓰는 한국 관장이나
  // 한국어로 잡힌 해외 기기가 **앱 안에서** 바꿀 곳이 없었다(전환 버튼이 첫 화면에만). 쿠키 1년이 판정을 덮는다.
  // 🔑 새로고침하지 않는다 — router.refresh 로 서버가 새 언어 문구만 다시 내려 준다. 카메라 전송·보기 통화와
  //    재생 제어 소켓이 그대로 산다(새로고침하면 통화가 전부 끊긴다).
  const locale = useLocale();
  const router = useRouter();
  const pickLanguage = (next: "ko" | "en") => {
    if (next === locale) return;
    document.cookie = `NEXT_LOCALE=${next};path=/;max-age=31536000;SameSite=Lax`;
    router.refresh();
  };
  // 자동 시연(박람회) 접기 — 기본 접힘. 데모·테스트 계정에서만 뜨는 줄이라 설정을 열 때마다
  // 코스 목록이 화면을 먹지 않게 한다. 펼침은 이 화면에 머무는 동안만(저장하지 않는다).
  const [demoOpen, setDemoOpen] = useState(false);
  // 영상통화 프레임 피커 — 전체 공개. 선택은 이 기기(내 폰) localStorage 에만 저장.
  const showFramePicker = canPickCallFrame(owner.email);
  const [callFrame, setCallFrame] = useState(DEFAULT_CALL_FRAME);
  useEffect(() => { setCallFrame(readCallFrame()); }, []); // 클라 마운트 후 실제 저장값 반영(SSR 안전)
  const pickFrame = (id: string) => {
    try { localStorage.setItem(CALL_FRAME_KEY, id); } catch {}
    setCallFrame(id);
  };
  // 이 기기에 받아둔 미러본 용량 — 0 이면 섹션 자체를 안 보여준다(설명할 것도 없는 상태)
  const [cacheBytes, setCacheBytes] = useState(0);
  useEffect(() => { void cacheUsage().then((u) => setCacheBytes(u.bytes)); }, []);
  const clearCached = () => { void clearCache().then(() => setCacheBytes(0)); };
  // 학생(재생기기) 화면에 내 카메라를 작게(우측 하단 구석) 띄울지 — 이 기기 localStorage. 기본 크게(중앙).
  // ('작은 창으로 전송 시작' 설정은 없앴다 — 항상 작은 창으로 시작하고 창에서 '크게'로 바꾼다.)
  // 이건 그것과 별개: 학생 화면에 내가 얼마나 크게 뜨나다(창에서 못 바꾸므로 설정으로 남긴다).
  const [playerSmall, setPlayerSmall] = useState(false);
  useEffect(() => { setPlayerSmall(readCallPlayerSize() === "small"); }, []); // SSR 안전
  // 🛑 '카메라 화질 개선 (실험)' 스위치는 2026-08-28 에 **없앴다** — 기본이 꺼짐이라 실제 관장은 아무도
  //    안 켰고(운영 표본에 H264 가 찍힌 날은 우리가 시험한 이틀뿐), 설정 줄 하나 값어치가 0이었다.
  //    이제 애플이 안 낀 짝이면 그냥 H.264 로 붙는다(lib/call-frame.ts · page.tsx h264For). 되살리지 말 것.
  const togglePlayerSize = () => {
    setPlayerSmall((prev) => {
      const next = !prev;
      try { localStorage.setItem(CALL_PLAYER_SIZE_KEY, next ? "small" : "big"); } catch {}
      return next;
    });
  };
  return (
    <div className="screen col" style={{ height: "100%" }}>
      {/* 플로팅 스크롤반응 헤더 — 최상단 반투명 → 내릴수록 불투명(제어화면과 통일) */}
      <ScrollSolidify className="frame__scroll col" style={{ padding: 0 }}>
        <div className="ctl-hd-sticky"><AppBar title={t("title")} /></div>
        <div className="col" style={{ padding: "4px 18px 22px", gap: 22 }}>
        {/* 프로필 카드 — 섹션 카드보다 한 단계 강조된 '대표' 카드(accent 워시·깊은 부양·테두리 없음).
            탭하면 '내 계정'(이름·이메일·연락처·로그아웃·회원 탈퇴). 계정 관련이 카드와 아래 섹션으로
            흩어져 있던 걸 한 곳에 모았다 — 되돌릴 수 없는 회원 탈퇴도 한 단계 안쪽으로 들어간다. */}
        <button onClick={onOpenAccount} className="row gap-12" style={{
          width: "100%", textAlign: "left",
          background: "linear-gradient(152deg, color-mix(in srgb, var(--accent) 9%, var(--surface-1)), var(--surface-1) 62%)",
          border: "none",
          borderRadius: "var(--r-lg)", padding: 18, cursor: "pointer",
          boxShadow: "var(--shadow-pop), inset 0 1px 0 var(--glass-hi)",
        }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "color-mix(in srgb, var(--accent) 14%, var(--surface-1))", color: "var(--accent)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 20, boxShadow: "0 0 0 3px color-mix(in srgb, var(--accent) 10%, transparent)", flex: "none" }}>{owner.name?.trim()?.[0] ?? "?"}</div>
          <div className="col gap-2" style={{ flex: 1, minWidth: 0 }}>
            <div className="row gap-8" style={{ minWidth: 0 }}>
              {owner.name?.trim()
                ? <span style={{ fontWeight: 750, fontSize: 17, letterSpacing: "-.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{owner.name}</span>
                : <span style={{ fontWeight: 750, fontSize: 17, color: "var(--accent)" }}>{t("addName")}</span>}
              {/* 내 권한 — 설정 목록에서 빼고 여기 프로필 카드로 통합 */}
              <span className={"chip " + (canManage ? "chip--accent" : "chip--off")} style={{ flex: "none" }}>
                {dojang.role === "OWNER" ? tr("owner") : dojang.role === "SUPERADMIN" ? tr("operator") : tr("instructor")}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 13 }}>{owner.email}</div>
          </div>
          <I.chevR size={18} className="dim" />
        </button>
        {/* 사용법 둘러보기 — 화면을 밝혀 가며 "여기를 눌러보세요"로 한 바퀴 돈다.
            ⭐ 2026-08-27 전체 공개. 자리는 **여기 그대로** — 프로필 카드 바로 아래, 섹션 라벨 없는 단독 줄.
               이 줄의 값어치는 전부 '처음 쓰는 관장이 발견하는 것'이라, 맨 아래로 내리면 값어치가 사라진다
               (사용자 결정: 맨 아래 '앱' 섹션·'도움말' 섹션 신설안을 두고 이 자리를 골랐다).
            영문은 칸마다 tour-steps.ts 의 en, 엔진 글은 messages control.tour(09-30). */}
        {onStartTour && (
          <GlassGroup inset={47}>
            <Row icon={<I.eye size={19} />} label={tt("row")} onClick={onStartTour} />
          </GlassGroup>
        )}
        {/* 자동 시연 — 누르면 그 코스가 사람 없이 저 혼자 한 바퀴 돈다(박람회 부스용).
            🛑 위 '둘러보기'는 전체 공개지만 이쪽은 **데모·테스트 계정 전용**이다(lib/flags demoAllowed).
               실제 관장 폰에서 눌리면 앱이 혼자 화면들을 조작한다 — 수업 중이면 사고다.
            ⭐ 2026-08-28 **평소엔 접어 둔다**(사용자 지시) — 데모·테스트 계정으로 설정을 열 때마다
               코스 넉 줄 + 설명 문단이 화면 위쪽을 통째로 먹었다. 접힌 줄 하나를 눌러야 코스가 나온다.
               🛑 코스 목록·설명을 다시 펼쳐진 채로 두지 말 것(섹션 라벨도 이 줄이 대신한다).
            ⚠️ 위 '둘러보기'와 헷갈리지 않게 갈라 놓는다: 둘러보기는 **사람이 눌러 가며**,
               시연은 **저절로** 돈다. 이름 옆 초 표시는 부스에서 어느 걸 틀지 고르는 기준이다. */}
        {demoCourses?.length && onStartDemo ? (
          <div className="col" style={{ gap: 12 }}>
            <GlassGroup inset={47}>
              {/* 접힌 줄 — 펼침 여부는 화살표 방향으로만 말한다(글자를 늘리지 않는다). */}
              <button onClick={() => setDemoOpen((v) => !v)} aria-expanded={demoOpen} className="row gap-12" style={{
                width: "100%", textAlign: "left", background: "transparent", border: "none",
                padding: "15px 16px", cursor: "pointer", color: "var(--text-1)",
              }}>
                <I.play size={19} />
                <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em", flex: 1 }}>자동 시연 (박람회)</span>
                <span className="muted" style={{ fontSize: 13.5 }}>{demoCourses.length}개</span>
                <I.chevD size={18} className="dim" style={{ transform: demoOpen ? "rotate(180deg)" : "none", transition: "transform .18s" }} />
              </button>
              {demoOpen ? demoCourses.map((c) => (
                // ⚠️ 100초가 넘으면 "204초"로는 길이가 안 잡힌다 — 분으로 끊어 준다.
                // ⭐ 계속 반복하는 코스는 길이를 적지 않는다 — 끝나는 시각이 없다(그만보기까지 돈다).
                <Row key={c.id} icon={<I.play size={19} />} label={c.label}
                  value={c.loop ? "계속 반복" : c.secs >= 100 ? `${Math.floor(c.secs / 60)}분 ${c.secs % 60}초` : `${c.secs}초`}
                  onClick={() => onStartDemo(c)} />
              )) : null}
            </GlassGroup>
            {demoOpen && (
              <div className="muted" style={{ fontSize: 12.5, padding: "0 6px", lineHeight: 1.5 }}>
                {demoCourses.map((c) => c.desc).join(" · ")}
              </div>
            )}
          </div>
        ) : null}
        <div className="col" style={{ gap: 12 }}>
          <SecLabel>{t("secSchool")}</SecLabel>
          <GlassGroup inset={47}>
            {canManage && <Row icon={<I.settings size={19} />} label={t("renameSchool")} value={dojang.name} onClick={onRenameDojang} />}
            <Row icon={<I.clock size={19} />} label={t("trainingLog")} onClick={onOpenTraining} />
            {onOpenHomeDevices && <Row icon={<I.home size={19} />} label={t("homeDevices")} onClick={onOpenHomeDevices} />}
            {canManage && <Row icon={<I.user size={19} />} label={t("manageInstructors")} onClick={onOpenMembers} />}
            <Row icon={<I.monitor size={19} />} label={t("switchSchool")} onClick={onSwitchDojang} />
            {canManage && <Row icon={<I.trash size={19} />} label={t("deleteSchool")} danger onClick={onDeleteDojang} />}
          </GlassGroup>
        </div>
        <div className="col" style={{ gap: 12 }}>
          <SecLabel>{t("secDisplay")}</SecLabel>
          <GlassGroup inset={47}>
            <div className="row gap-12" style={{ padding: "13px 16px" }}>
              {theme === "light" ? <I.sun size={19} /> : <I.moon size={19} />}
              {/* 제목은 고정하고 지금 상태를 아래에 적는다 — 예전엔 '누르면 될 상태'를 제목에 썼더니
                  지금이 밝은 화면인지 누르면 밝아지는지 읽는 사람마다 갈렸다. */}
              <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("theme")}</span>
                <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{theme === "light" ? t("themeLight") : t("themeDark")}</span>
              </div>
              <button
                role="switch" aria-checked={theme === "light"} aria-label={t("themeLight")}
                className="switch" data-on={theme === "light"} onClick={onToggleTheme}
              >
                <span className="switch__knob" />
              </button>
            </div>
            {/* 언어 — 이름은 각 언어로 적는다(한국어 / English). 못 읽는 언어로 떠 있어도 제 언어 이름은 알아본다. */}
            <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
              <I.globe size={19} />
              <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("language")}</span>
              <div style={{ width: 176, flex: "none" }}>
                <Segmented<"ko" | "en">
                  value={locale === "en" ? "en" : "ko"}
                  onChange={pickLanguage}
                  options={[{ value: "ko", label: "한국어" }, { value: "en", label: "English" }]}
                />
              </div>
            </div>
            {/* 전체 화면 — 브라우저 탭으로 열었을 때만(useAppFullscreen). 홈 화면 앱·아이폰 사파리엔 줄이 없다.
                🚧 우선 테스트 계정에만(lib/flags appFullscreenAllowed → owner.appFullscreen). */}
            {owner.appFullscreen && fs.available && (
              <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
                <I.maximize size={19} />
                <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("fullscreen")}</span>
                  <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{t("fullscreenDesc")}</span>
                </div>
                <button role="switch" aria-checked={fs.on} aria-label={t("fullscreen")}
                  className="switch" data-on={fs.on} onClick={fs.toggle} style={{ flex: "none" }}>
                  <span className="switch__knob" />
                </button>
              </div>
            )}
            <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
              <I.film size={19} />
              <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("titleStay")}</span>
                <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{t("titleStayDesc")}</span>
              </div>
              {/* '영상 제목 계속 표시'(09-21) — 예전 '재생 화면에 제목 표시' 켜기/끄기 자리. 끄면 잠깐(기본), 켜면 계속. */}
              <button
                role="switch" aria-checked={dojang.titleStay === true} aria-label={t("titleStay")}
                className="switch" data-on={dojang.titleStay === true} onClick={onToggleTitleStay} style={{ flex: "none" }}
              >
                <span className="switch__knob" />
              </button>
            </div>
            {/* '동작 이름 자막 표시' — 2026-09-10 트랙이 0개라 숨겼다가(사용자 "우선 안 해도 돼"), 09-16 음성인식으로
                전체·품마디·품 트랙을 채우고 되살림(scripts/caption-autogen.mts). 기본은 꺼짐(Dojang.captions). */}
            <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
              <I.film size={19} />
              <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("showCaptions")}</span>
                <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{t("showCaptionsDesc")}</span>
              </div>
              <button role="switch" aria-checked={dojang.captions === true} aria-label={t("showCaptions")}
                className="switch" data-on={dojang.captions === true} onClick={onToggleCaptions} style={{ flex: "none" }}>
                <span className="switch__knob" />
              </button>
            </div>
            {/* 자막 언어 — 자막을 켰을 때만. 이름은 각 언어로(English / Deutsch). 한국어는 없다(영상에 말풍선으로 이미 있음). */}
            {dojang.captions === true && onCaptionLang && (
              <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
                <I.globe size={19} />
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("captionLang")}</span>
                <div style={{ width: 176, flex: "none" }}>
                  <Segmented<"en" | "de">
                    value={dojang.captionLang === "de" ? "de" : "en"}
                    onChange={onCaptionLang}
                    options={[{ value: "en", label: "English" }, { value: "de", label: "Deutsch" }]}
                  />
                </div>
              </div>
            )}
          </GlassGroup>
        </div>
        {/* 영상 — 미리보기로 본 영상은 이 기기에 저절로 쌓인다(lib/mirror-cache.ts). 예산 안에서 알아서
            오래된 것부터 버리므로 평소엔 손댈 일이 없지만, 저장된 게 있을 때만 비우는 길을 열어 둔다. */}
        {cacheBytes > 0 && (
          <div className="col" style={{ gap: 12 }}>
            <SecLabel>{t("secVideo")}</SecLabel>
            <GlassGroup inset={47}>
              <Row icon={<I.trash size={19} />} label={t("clearCache")}
                value={`${Math.max(1, Math.round(cacheBytes / 1048576))}MB`} onClick={clearCached} />
            </GlassGroup>
            <div className="muted" style={{ fontSize: 12.5, padding: "0 6px", lineHeight: 1.5 }}>{t("clearCacheDesc")}</div>
          </div>
        )}
        {/* 카메라 — '내 카메라 전송'(학생 기기로 시범 송출) 설정. 카메라를 쓸 수 있는 계정에게만 보인다. */}
        {owner.camera && (
          <div className="col" style={{ gap: 12 }}>
            <SecLabel>{t("secCamera")}</SecLabel>
            <GlassGroup inset={47}>
              <div className="row gap-12" style={{ padding: "13px 16px", alignItems: "center" }}>
                <I.monitor size={19} />
                <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{t("showSmall")}</span>
                  <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{t("showSmallDesc")}</span>
                </div>
                <button
                  role="switch" aria-checked={playerSmall} aria-label={t("showSmall")}
                  className="switch" data-on={playerSmall} onClick={togglePlayerSize} style={{ flex: "none" }}
                >
                  <span className="switch__knob" />
                </button>
              </div>
            </GlassGroup>
          </div>
        )}
        {/* 영상통화 프레임 — '내 카메라 전송'으로 학생 기기에 시범을 보일 때 영상 둘레에 씌우는 장식을
            고른다. 전체 공개. 선택은 이 기기(내 폰)에만 저장(localStorage). */}
        {showFramePicker && (
          <div className="col" style={{ gap: 12 }}>
            <SecLabel>{t("secCallFrame")}</SecLabel>
            <GlassGroup inset={47}>
              {CALL_FRAMES.map((f) => {
                const on = callFrame === f.id;
                // 이름·설명은 문구 파일(callFrame_<id>) — lib/call-frame 의 한국어 label/desc 는 문구가 없는 새 프레임의 폴백.
                // (예전엔 lib 값을 그대로 찍어 영문 화면에도 '오로라 글라스'가 떴다 — 09-21 사용자 "Video Call Frame 부분 영문 변환이 안 됐네")
                const label = t.has(`callFrame_${f.id}`) ? t(`callFrame_${f.id}`) : f.label;
                const desc = t.has(`callFrame_${f.id}_desc`) ? t(`callFrame_${f.id}_desc`) : f.desc;
                return (
                  <button key={f.id} onClick={() => pickFrame(f.id)} className="row gap-12"
                    role="radio" aria-checked={on} aria-label={label}
                    style={{
                      width: "100%", textAlign: "left", border: "none", padding: "13px 16px", cursor: "pointer", color: "var(--text-1)",
                      // 선택 상태는 은은한 accent tint 로만 — iOS 설정처럼 '행 전체가 살짝 물든' 느낌. 체크는 조용한 조연.
                      background: on ? "color-mix(in srgb, var(--accent) 5%, transparent)" : "transparent",
                    }}>
                    <div className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
                      <span style={{ fontWeight: 600, fontSize: 16, letterSpacing: "-.01em" }}>{label}</span>
                      <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{desc}</span>
                    </div>
                    {/* 선택 표시는 tint 가 주역 — 체크는 작고(14) 가늘게·행 오른쪽 끝에 조용히 얹기만. */}
                    {on && <I.check size={14} sw={1.8} style={{ color: "var(--accent)", opacity: 0.55, flex: "none", marginRight: -2 }} />}
                  </button>
                );
              })}
            </GlassGroup>
            <span className="dim" style={{ fontSize: 12.5, lineHeight: 1.5, padding: "0 4px" }}>
              {t("callFrameDesc")}
            </span>
          </div>
        )}
        {/* 맨 아래 '앱' 섹션 — 랜딩(시작 화면)의 '관리 앱 / 수련 화면' 카드로 되돌아가는 동작.
            섹션 없이 홀로 떠 있던 '메인 페이지로'를 다른 설정과 같은 위계로 녹이고,
            웹사이트 톤 대신 실제 기능(어떤 앱으로 들어갈지 다시 고르기)이 드러나는 이름으로. */}
        <div className="col" style={{ gap: 12 }}>
          <SecLabel>{t("secApp")}</SecLabel>
          <GlassGroup inset={47}>
            <Row icon={<I.grid size={19} />} label={t("startScreen")} onClick={onGoHome} />
          </GlassGroup>
        </div>
        <div className="dim" style={{ fontSize: 12, textAlign: "center", paddingTop: 4 }}>easy-tedu</div>
        </div>
      </ScrollSolidify>
    </div>
  );
}
